<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-50" @click="close"></div>

    <!-- Panel (wider for better data tables) -->
    <div class="fixed right-0 top-0 h-full w-[720px] max-w-[85vw] bg-gray-900 text-gray-100 shadow-xl overflow-y-auto">
      <div class="p-5">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <div class="flex items-center gap-4">
            <h2 class="text-lg font-semibold text-yellow-400">Developer Panel</h2>
            <router-link to="/metrics" @click="close" class="text-xs text-yellow-300 hover:text-yellow-100 transition-colors">View Metrics &rarr;</router-link>
          </div>
          <button @click="close" class="text-gray-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Password Gate -->
        <div v-if="devPanelAuth.required && !devPanelAuth.verified" class="flex flex-col items-center justify-center py-20">
          <div class="w-80">
            <p class="text-sm text-gray-400 mb-4 text-center">Enter the DevPanel password to continue</p>
            <form @submit.prevent="verifyDevPanelPassword" class="space-y-3">
              <input
                v-model="devPanelAuth.password"
                type="password"
                placeholder="Password"
                autocomplete="off"
                class="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              />
              <button
                type="submit"
                :disabled="devPanelAuth.checking || !devPanelAuth.password"
                class="w-full px-3 py-2 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded transition-colors"
              >
                {{ devPanelAuth.checking ? 'Verifying...' : 'Unlock' }}
              </button>
              <p v-if="devPanelAuth.error" class="text-red-400 text-xs text-center">{{ devPanelAuth.error }}</p>
            </form>
          </div>
        </div>

        <!-- Panel Content (hidden until verified) -->
        <div v-else-if="devPanelAuth.required === null" class="flex justify-center py-20">
          <span class="text-sm text-gray-500">Checking access...</span>
        </div>

        <template v-else>
        <!-- ============================================================ -->
        <!-- SECTION 1: SYNERGIST -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <!-- Accordion Header -->
          <button
            @click="toggleSection('synergist')"
            class="w-full flex items-center justify-between py-3 px-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('synergist') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-gray-200 uppercase tracking-wide">Synergist</span>
            </div>
            <span
              :class="synergist.status.enabled ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'"
              class="text-xs px-2 py-0.5 rounded-full font-medium"
            >
              {{ synergist.status.enabled ? 'Enabled' : 'Disabled' }}
            </span>
          </button>

          <!-- Accordion Content -->
          <div v-show="openSections.has('synergist')" class="mt-3 pl-2 space-y-4">

            <!-- Sub-section A: Connection & Status -->
            <div>
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Connection & Status</h4>
              <div class="bg-gray-800 rounded-lg p-3 mb-3">
                <div class="grid grid-cols-2 gap-x-6 gap-y-2">
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-gray-400">Status</span>
                    <span :class="synergist.status.enabled ? 'text-green-400' : 'text-red-400'" class="text-sm font-medium">
                      {{ synergist.status.enabled ? 'Enabled' : 'Disabled' }}
                    </span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-gray-400">Mode</span>
                    <span :class="synergist.status.readOnly ? 'text-yellow-400' : 'text-green-400'" class="text-sm font-medium">
                      {{ synergist.status.readOnly ? 'Read-Only' : 'Read/Write' }}
                    </span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-gray-400">Credentials</span>
                    <span :class="synergist.status.hasCredentials ? 'text-green-400' : 'text-red-400'" class="text-sm font-medium">
                      {{ synergist.status.hasCredentials ? 'Configured' : 'Missing' }}
                    </span>
                  </div>
                  <div v-if="synergist.status.testJobs?.length" class="flex justify-between items-center">
                    <span class="text-sm text-gray-400">Test Jobs</span>
                    <span class="text-sm text-gray-300">{{ synergist.status.testJobs.join(', ') }}</span>
                  </div>
                </div>
              </div>

              <button
                @click="testSynergist"
                :disabled="synergist.testing"
                class="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
              >
                <span v-if="synergist.testing">Testing...</span>
                <span v-else>Test Connection</span>
              </button>

              <div v-if="synergist.result" class="mt-3 p-3 rounded-lg text-sm" :class="synergist.result.connected ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'">
                <div class="font-medium mb-1">
                  {{ synergist.result.connected ? 'Connected' : 'Connection Failed' }}
                </div>
                <div v-if="synergist.result.connected" class="text-xs opacity-75">
                  {{ synergist.result.totalJobs }} jobs available
                </div>
                <div v-if="synergist.result.error" class="text-xs opacity-75">
                  {{ synergist.result.error }}
                </div>
              </div>
            </div>

            <!-- Sub-section B: Quick Lookups -->
            <div class="pt-3 border-t border-gray-700">
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Quick Lookups</h4>
              <div class="grid grid-cols-2 gap-2 mb-3">
                <button
                  @click="fetchJobs"
                  :disabled="synergist.loadingJobs"
                  class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-3 rounded-lg text-sm transition-colors text-left"
                >
                  <span v-if="synergist.loadingJobs">Loading...</span>
                  <span v-else>Fetch Jobs (first 10)</span>
                </button>

                <button
                  @click="fetchStageTypes"
                  :disabled="synergist.loadingStageTypes"
                  class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-3 rounded-lg text-sm transition-colors text-left"
                >
                  <span v-if="synergist.loadingStageTypes">Loading...</span>
                  <span v-else>Fetch Stage Types <span class="text-yellow-400 text-xs ml-1">ENV</span></span>
                </button>

                <button
                  @click="fetchChargeCodes"
                  :disabled="synergist.loadingChargeCodes"
                  class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-3 rounded-lg text-sm transition-colors text-left"
                >
                  <span v-if="synergist.loadingChargeCodes">Loading...</span>
                  <span v-else>Fetch Charge Codes</span>
                </button>
              </div>

              <!-- Jobs Preview -->
              <div v-if="synergist.jobs.length" class="mb-3">
                <div class="text-xs text-gray-400 mb-1">Jobs (first 10):</div>
                <div class="bg-gray-800 rounded-lg max-h-40 overflow-y-auto">
                  <div v-for="job in synergist.jobs" :key="job.number" class="p-2 border-b border-gray-700 last:border-0">
                    <div class="text-sm font-medium text-gray-200">{{ job.number }}</div>
                    <div class="text-xs text-gray-400 truncate">{{ job.description }}</div>
                  </div>
                </div>
              </div>

              <!-- Stage Types Preview -->
              <div v-if="synergist.stageTypes.length" class="mb-3">
                <div class="text-xs text-gray-400 mb-1">Stage Types (use ID for SYNERGIST_WORKFLOW_TYPE .env):</div>
                <div class="bg-gray-800 rounded-lg max-h-40 overflow-y-auto">
                  <div v-for="type in synergist.stageTypes" :key="type.id" class="p-2 border-b border-gray-700 last:border-0">
                    <div class="text-sm text-gray-200">
                      <span class="text-indigo-400 font-mono">{{ type.id }}</span> - {{ type.name }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Charge Codes Preview -->
              <div v-if="synergist.chargeCodesError" class="mb-3 p-3 bg-red-900/50 text-red-300 rounded-lg text-sm">
                <div class="font-medium mb-1">Charge Codes Error</div>
                <div class="text-xs opacity-75">{{ synergist.chargeCodesError }}</div>
              </div>
              <div v-if="synergist.chargeCodes.length" class="mb-3">
                <div class="text-xs text-gray-400 mb-1">Charge Codes ({{ synergist.chargeCodes.length }} found):</div>
                <div class="bg-gray-800 rounded-lg max-h-48 overflow-y-auto">
                  <table class="w-full text-sm">
                    <thead class="text-xs text-gray-500 uppercase">
                      <tr class="border-b border-gray-700">
                        <th class="text-left p-2">Code</th>
                        <th class="text-left p-2">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="cc in synergist.chargeCodes" :key="cc.code || cc.uuid" class="border-b border-gray-700 last:border-0">
                        <td class="p-2 text-indigo-400 font-mono">{{ cc.code }}</td>
                        <td class="p-2 text-gray-300">{{ cc.description }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Sub-section C: API Explorers -->
            <div class="pt-3 border-t border-gray-700">
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">API Explorers</h4>

              <!-- Test Stage Details -->
              <div class="mb-4">
                <div class="text-xs text-gray-400 mb-1">Stage Details</div>
                <div class="flex gap-2 mb-2">
                  <input
                    v-model="synergist.stageCodeInput"
                    placeholder="e.g. 2/W0000001234"
                    class="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                  <button
                    @click="fetchStageDetails"
                    :disabled="synergist.loadingStageDetails || !synergist.stageCodeInput"
                    class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white py-1.5 px-3 rounded text-sm transition-colors"
                  >
                    {{ synergist.loadingStageDetails ? '...' : 'Fetch' }}
                  </button>
                </div>
                <div v-if="synergist.stageDetailsError" class="text-xs text-red-400 mb-2">
                  {{ synergist.stageDetailsError }}
                </div>
                <div v-if="synergist.stageDetails" class="bg-gray-800 rounded-lg p-2 max-h-48 overflow-y-auto">
                  <div class="text-xs text-gray-400 mb-1">Fields returned:</div>
                  <div class="text-xs text-green-400 font-mono mb-2">
                    {{ Object.keys(synergist.stageDetails).join(', ') }}
                  </div>
                  <pre class="text-xs text-gray-300 whitespace-pre-wrap">{{ JSON.stringify(synergist.stageDetails, null, 2) }}</pre>
                </div>
              </div>

              <!-- Phase Model Structure Discovery -->
              <div class="mb-4">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-xs text-gray-400">Phase Model Structure</span>
                  <span class="text-xs bg-amber-900 text-amber-300 px-1.5 py-0.5 rounded">Discovery</span>
                </div>
                <p class="text-xs text-gray-500 mb-2">Discover all editable fields on the phase model (comments, custom fields, etc.)</p>
                <div class="flex gap-2 mb-2">
                  <input
                    v-model="synergist.phaseModelInput"
                    placeholder="e.g. 1/00200399.010"
                    class="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                  <button
                    @click="fetchPhaseModelStructure"
                    :disabled="synergist.loadingPhaseModel || !synergist.phaseModelInput"
                    class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white py-1.5 px-3 rounded text-sm transition-colors"
                  >
                    {{ synergist.loadingPhaseModel ? '...' : 'Fetch' }}
                  </button>
                </div>
                <div v-if="synergist.phaseModelError" class="text-xs text-red-400 mb-2">
                  {{ synergist.phaseModelError }}
                </div>
                <div v-if="synergist.phaseModelFields.length" class="bg-gray-800 rounded-lg max-h-80 overflow-y-auto">
                  <div class="text-xs text-gray-400 p-2 border-b border-gray-700">
                    {{ synergist.phaseModelFields.length }} fields found
                    ({{ synergist.phaseModelFields.filter(f => f.editable).length }} editable,
                    {{ synergist.phaseModelFields.filter(f => f.highlight).length }} potential comment/note fields)
                  </div>
                  <table class="w-full text-xs">
                    <thead class="text-gray-500 uppercase sticky top-0 bg-gray-800">
                      <tr class="border-b border-gray-700">
                        <th class="text-left p-2">Field Name</th>
                        <th class="text-left p-2">Label</th>
                        <th class="text-left p-2">Type</th>
                        <th class="text-center p-2">Editable</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="field in synergist.phaseModelFields"
                        :key="field.name"
                        class="border-b border-gray-700 last:border-0"
                        :class="{
                          'bg-amber-900/30 border-l-2 border-l-amber-500': field.highlight && field.editable,
                          'bg-amber-900/15': field.highlight && !field.editable,
                          'bg-green-900/20': field.editable && !field.highlight
                        }"
                      >
                        <td class="p-2 font-mono text-[11px]" :class="field.editable ? 'text-green-400' : 'text-gray-400'">{{ field.name }}</td>
                        <td class="p-2" :class="field.highlight ? 'text-amber-300' : 'text-gray-300'">{{ field.label }}</td>
                        <td class="p-2 text-gray-500">{{ field.type }}</td>
                        <td class="p-2 text-center">
                          <span v-if="field.editable" class="text-green-400 font-semibold">Yes</span>
                          <span v-else class="text-gray-600">—</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Phase Raw Data (Field Value Discovery) -->
              <div class="mb-4">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-xs text-gray-400">Phase Raw Data</span>
                  <span class="text-xs bg-amber-900 text-amber-300 px-1.5 py-0.5 rounded">Discovery</span>
                </div>
                <p class="text-xs text-gray-500 mb-2">Fetch ALL field values for a phase. Use to find which field holds a known text string.</p>
                <div class="flex gap-2 mb-2">
                  <input
                    v-model="synergist.phaseRawInput"
                    placeholder="e.g. 1/00200399.010"
                    class="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                  <button
                    @click="fetchPhaseRawData"
                    :disabled="synergist.loadingPhaseRaw || !synergist.phaseRawInput"
                    class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white py-1.5 px-3 rounded text-sm transition-colors"
                  >
                    {{ synergist.loadingPhaseRaw ? '...' : 'Fetch' }}
                  </button>
                </div>
                <div v-if="synergist.phaseRawError" class="text-xs text-red-400 mb-2">
                  {{ synergist.phaseRawError }}
                </div>
                <div v-if="synergist.phaseRawFields.length">
                  <div class="mb-2">
                    <input
                      v-model="synergist.phaseRawSearch"
                      placeholder="Search field values..."
                      class="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200 placeholder-gray-500"
                    />
                  </div>
                  <div class="bg-gray-800 rounded-lg max-h-80 overflow-y-auto">
                    <div class="text-xs text-gray-400 p-2 border-b border-gray-700">
                      {{ filteredPhaseRawFields.length }} of {{ synergist.phaseRawFields.length }} fields
                      <span v-if="synergist.phaseRawSearch" class="text-amber-400 ml-1">
                        (filtered by "{{ synergist.phaseRawSearch }}")
                      </span>
                    </div>
                    <table class="w-full text-xs">
                      <thead class="text-gray-500 uppercase sticky top-0 bg-gray-800">
                        <tr class="border-b border-gray-700">
                          <th class="text-left p-2 w-1/3">Field</th>
                          <th class="text-left p-2">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="field in filteredPhaseRawFields"
                          :key="field.name"
                          class="border-b border-gray-700 last:border-0"
                          :class="{
                            'bg-amber-900/30 border-l-2 border-l-amber-500': field.matchesSearch && synergist.phaseRawSearch,
                            'bg-amber-900/15': /description|comment|note|custom/i.test(field.name) && !field.matchesSearch
                          }"
                        >
                          <td class="p-2 font-mono text-[11px] text-gray-400 align-top">{{ field.name }}</td>
                          <td class="p-2 text-gray-200 break-all whitespace-pre-wrap" :class="{ 'text-amber-300 font-semibold': field.matchesSearch && synergist.phaseRawSearch }">{{ field.value === '' ? '(empty)' : field.value }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Test Phase Field Update -->
              <div class="mb-4">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-xs text-gray-400">Test Phase Update</span>
                  <span class="text-xs bg-red-900 text-red-300 px-1.5 py-0.5 rounded">Write</span>
                </div>
                <p class="text-xs text-gray-500 mb-2">Test writing to a phase field. Fetch Raw Data first to get the phaseUuid. Respects readonly + test jobs guard.</p>
                <div class="grid grid-cols-2 gap-2 mb-2">
                  <input
                    v-model="synergist.testUpdateJobAndPhase"
                    placeholder="jobAndPhase (e.g. 1/00200399.010)"
                    class="bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                  <input
                    v-model="synergist.testUpdateUuid"
                    placeholder="phaseUuid (from Raw Data)"
                    class="bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                  <input
                    v-model="synergist.testUpdateField"
                    placeholder="fieldName (e.g. phaseComments)"
                    class="bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                  <input
                    v-model="synergist.testUpdateValue"
                    placeholder="value to write"
                    class="bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                </div>
                <button
                  @click="testPhaseUpdate"
                  :disabled="synergist.loadingTestUpdate || !synergist.testUpdateJobAndPhase || !synergist.testUpdateUuid || !synergist.testUpdateField"
                  class="bg-red-900 hover:bg-red-800 disabled:bg-gray-800 disabled:text-gray-500 text-white py-1.5 px-3 rounded text-sm transition-colors mb-2"
                >
                  {{ synergist.loadingTestUpdate ? '...' : 'Test Write' }}
                </button>
                <div v-if="synergist.testUpdateError" class="text-xs text-red-400 mb-2">
                  {{ synergist.testUpdateError }}
                </div>
                <div v-if="synergist.testUpdateResult" class="rounded-lg p-3 text-xs mb-2" :class="synergist.testUpdateResult.dryRun ? 'bg-yellow-900/50 text-yellow-300' : 'bg-green-900/50 text-green-300'">
                  <div v-if="synergist.testUpdateResult.dryRun" class="font-semibold mb-1">
                    Blocked ({{ synergist.testUpdateResult.reason }})
                  </div>
                  <div v-else class="font-semibold mb-1">Write attempted</div>
                  <pre class="whitespace-pre-wrap text-gray-300">{{ JSON.stringify(synergist.testUpdateResult, null, 2) }}</pre>
                </div>
              </div>

              <!-- Time Summary (Estimates) Explorer -->
              <div class="mb-4">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-xs text-gray-400">Time Summary (Estimates)</span>
                  <span class="text-xs bg-amber-900 text-amber-300 px-1.5 py-0.5 rounded">Discovery</span>
                </div>
                <p class="text-xs text-gray-500 mb-2">Fetch estimate records for a phase. Shows timesummaryInternal IDs needed for Quote Import.</p>
                <div class="flex gap-2 mb-2">
                  <input
                    v-model="synergist.estimatesInput"
                    placeholder="e.g. 1/00200399.010"
                    class="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                  <button
                    @click="fetchEstimates"
                    :disabled="synergist.loadingEstimates || !synergist.estimatesInput"
                    class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white py-1.5 px-3 rounded text-sm transition-colors"
                  >
                    {{ synergist.loadingEstimates ? '...' : 'Fetch' }}
                  </button>
                </div>
                <div v-if="synergist.estimatesError" class="text-xs text-red-400 mb-2">
                  {{ synergist.estimatesError }}
                </div>
                <div v-if="synergist.estimatesResult !== null && !synergist.estimatesError">
                  <div v-if="synergist.estimatesResult.length === 0" class="p-3 bg-yellow-900/30 border border-yellow-700 rounded-lg text-xs text-yellow-300">
                    No estimate records found. PM must create via Quick Estimate in Synergist first.
                  </div>
                  <div v-else class="bg-gray-800 rounded-lg max-h-64 overflow-y-auto">
                    <div class="text-xs text-gray-400 p-2 border-b border-gray-700">
                      {{ synergist.estimatesResult.length }} estimate lines found
                    </div>
                    <table class="w-full text-xs">
                      <thead class="text-gray-500 uppercase sticky top-0 bg-gray-800">
                        <tr class="border-b border-gray-700">
                          <th class="text-left p-2">Internal ID</th>
                          <th class="text-left p-2">UUID</th>
                          <th class="text-left p-2">Stage</th>
                          <th class="text-left p-2">Charge Code</th>
                          <th class="text-left p-2">Description</th>
                          <th class="text-right p-2">Est. Hours</th>
                          <th class="text-right p-2">Rate</th>
                          <th class="text-right p-2">Est. Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="est in synergist.estimatesResult" :key="est.uuid || est.internal" class="border-b border-gray-700 last:border-0">
                          <td class="p-2 text-indigo-400 font-mono font-bold">{{ est.internal }}</td>
                          <td class="p-2 text-purple-400 font-mono text-[10px] truncate max-w-[100px]" :title="est.uuid">{{ est.uuid }}</td>
                          <td class="p-2 text-gray-300 truncate max-w-[120px]">{{ est.stageDescription }}</td>
                          <td class="p-2 text-gray-400 font-mono">{{ est.chargeCode }}</td>
                          <td class="p-2 text-gray-300">{{ est.chargeCodeDescription }}</td>
                          <td class="p-2 text-right text-gray-300">{{ est.estimatedUnits }}</td>
                          <td class="p-2 text-right text-gray-400">{{ est.chargeRate }}</td>
                          <td class="p-2 text-right text-gray-300">{{ est.estimatedCost }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Test Estimate Update -->
              <div class="mb-4">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-xs text-gray-400">Test Estimate Update</span>
                  <span class="text-xs bg-red-900 text-red-300 px-1.5 py-0.5 rounded">Write</span>
                </div>
                <p class="text-xs text-gray-500 mb-2">Test writing hours to an estimate record. Get IDs from the Estimates explorer above.</p>
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-xs text-gray-500">ID type:</span>
                  <button
                    @click="synergist.testEstimateIdType = 'internal'"
                    class="text-xs px-2 py-0.5 rounded transition-colors"
                    :class="synergist.testEstimateIdType === 'internal' ? 'bg-indigo-700 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'"
                  >Internal ID</button>
                  <button
                    @click="synergist.testEstimateIdType = 'uuid'"
                    class="text-xs px-2 py-0.5 rounded transition-colors"
                    :class="synergist.testEstimateIdType === 'uuid' ? 'bg-purple-700 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'"
                  >UUID</button>
                </div>
                <div class="grid grid-cols-2 gap-2 mb-2">
                  <input
                    v-model="synergist.testEstimateInternal"
                    :placeholder="synergist.testEstimateIdType === 'uuid' ? 'timesummaryUuid' : 'timesummaryInternal (e.g. 39177)'"
                    class="bg-gray-800 border rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                    :class="synergist.testEstimateIdType === 'uuid' ? 'border-purple-600' : 'border-gray-600'"
                  />
                  <input
                    v-model="synergist.testEstimateHours"
                    placeholder="hours (e.g. 8.5)"
                    type="number"
                    step="0.01"
                    class="bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                </div>
                <div class="grid grid-cols-2 gap-2 mb-2">
                  <input
                    v-model="synergist.testEstimateJobCode"
                    placeholder="jobCode for guard (e.g. TEST9999)"
                    class="bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                  <input
                    v-model="synergist.testEstimateJobAndPhase"
                    placeholder="jobAndPhase for diff (e.g. 2/TEST9999.001)"
                    class="bg-gray-800 border border-cyan-800 rounded px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500"
                  />
                </div>
                <p v-if="synergist.testEstimateJobAndPhase" class="text-[10px] text-cyan-500 mb-2">Before/after diff enabled — will fetch record before and after write to show exactly what changed</p>
                <button
                  @click="testEstimateUpdate"
                  :disabled="synergist.loadingTestEstimate || !synergist.testEstimateInternal || (synergist.testEstimateHours === '' || synergist.testEstimateHours === null)"
                  class="bg-red-900 hover:bg-red-800 disabled:bg-gray-800 disabled:text-gray-500 text-white py-1.5 px-3 rounded text-sm transition-colors mb-2"
                >
                  {{ synergist.loadingTestEstimate ? '...' : 'Test Write Hours (with diff)' }}
                </button>
                <div v-if="synergist.testEstimateError" class="text-xs text-red-400 mb-2">
                  {{ synergist.testEstimateError }}
                </div>
                <div v-if="synergist.testEstimateResult" class="rounded-lg p-3 text-xs mb-2" :class="synergist.testEstimateResult.dryRun ? 'bg-yellow-900/50 text-yellow-300' : 'bg-green-900/50 text-green-300'">
                  <div v-if="synergist.testEstimateResult.dryRun" class="font-semibold mb-1">
                    Blocked ({{ synergist.testEstimateResult.reason }})
                  </div>
                  <template v-else>
                    <div class="font-semibold mb-1">Write response: {{ synergist.testEstimateResult.writeResponse?.responsestatus || 'OK' }}</div>
                    <!-- Before/After summary -->
                    <div v-if="synergist.testEstimateResult.before || synergist.testEstimateResult.after" class="my-2 p-2 bg-gray-900 rounded border border-gray-700">
                      <div class="font-semibold text-gray-400 mb-1">Before / After:</div>
                      <div class="grid grid-cols-2 gap-2">
                        <div>
                          <span class="text-gray-500">Hours:</span>
                          <span class="text-red-400 ml-1">{{ synergist.testEstimateResult.before?.estimatedUnits ?? '?' }}</span>
                        </div>
                        <div>
                          <span class="text-gray-500">Hours:</span>
                          <span class="text-green-400 ml-1">{{ synergist.testEstimateResult.after?.estimatedUnits ?? '?' }}</span>
                        </div>
                      </div>
                    </div>
                    <!-- Full diff -->
                    <div v-if="synergist.testEstimateResult.diff" class="my-2">
                      <div v-if="synergist.testEstimateResult.diff._noChanges" class="p-2 bg-yellow-900/50 border border-yellow-700 rounded text-yellow-300 font-semibold">
                        NO FIELDS CHANGED — API said success but nothing actually changed
                      </div>
                      <div v-else>
                        <div class="font-semibold text-cyan-400 mb-1">Fields that changed:</div>
                        <div v-for="(change, fieldName) in synergist.testEstimateResult.diff" :key="fieldName" class="flex gap-2 py-0.5 border-b border-gray-800">
                          <span class="text-gray-400 font-mono min-w-[200px]">{{ fieldName }}</span>
                          <span class="text-red-400">{{ change.before }}</span>
                          <span class="text-gray-600">&rarr;</span>
                          <span class="text-green-400">{{ change.after }}</span>
                        </div>
                      </div>
                    </div>
                    <details class="mt-2">
                      <summary class="text-gray-500 cursor-pointer hover:text-gray-400">Raw response</summary>
                      <pre class="whitespace-pre-wrap text-gray-300 mt-1">{{ JSON.stringify(synergist.testEstimateResult, null, 2) }}</pre>
                    </details>
                  </template>
                </div>
              </div>

              <!-- Lookup Properties Explorer -->
              <div class="mb-2">
                <div class="text-xs text-gray-400 mb-1">Lookup Properties (custom fields)</div>
                <button
                  @click="fetchLookupProperties"
                  :disabled="synergist.loadingLookupProperties"
                  class="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-4 rounded-lg text-sm transition-colors text-left mb-2"
                >
                  <span v-if="synergist.loadingLookupProperties">Loading...</span>
                  <span v-else>Fetch Lookup Properties</span>
                </button>
                <div v-if="synergist.lookupProperties.length" class="bg-gray-800 rounded-lg max-h-40 overflow-y-auto mb-2">
                  <div
                    v-for="prop in synergist.lookupProperties"
                    :key="prop.uuid"
                    @click="selectLookupProperty(prop)"
                    class="p-2 border-b border-gray-700 last:border-0 cursor-pointer hover:bg-gray-700"
                    :class="synergist.selectedLookupProperty?.uuid === prop.uuid ? 'bg-indigo-900' : ''"
                  >
                    <div class="text-sm text-gray-200">{{ prop.label || '(no label)' }}</div>
                    <div class="text-xs text-gray-500 font-mono">UUID: {{ prop.uuid }}</div>
                  </div>
                </div>
                <div v-if="synergist.selectedLookupProperty" class="mt-2">
                  <div class="text-xs text-gray-400 mb-1">
                    Values for: <span class="text-indigo-400">{{ synergist.selectedLookupProperty.label }}</span>
                  </div>
                  <div v-if="synergist.loadingLookupValues" class="text-xs text-gray-500">Loading values...</div>
                  <div v-else-if="synergist.lookupValues.length" class="bg-gray-800 rounded-lg max-h-32 overflow-y-auto">
                    <div v-for="val in synergist.lookupValues" :key="val.uuid" class="p-2 border-b border-gray-700 last:border-0">
                      <span class="text-sm text-green-400">{{ val.value }}</span>
                    </div>
                  </div>
                  <div v-else class="text-xs text-gray-500">No values found</div>
                </div>
              </div>

              <!-- Raw Lookup Properties (Custom Field Metadata) -->
              <div class="mb-2 mt-4 border-t border-gray-700 pt-3">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-xs text-gray-400">Custom Field Metadata (Raw)</span>
                  <span class="text-xs bg-amber-900 text-amber-300 px-1.5 py-0.5 rounded">Discovery</span>
                </div>
                <p class="text-xs text-gray-500 mb-2">Fetch ALL metadata fields for lookup/custom field properties. Shows panel, group, section placement info if available.</p>
                <button
                  @click="fetchLookupPropertiesRaw"
                  :disabled="synergist.loadingLookupPropertiesRaw"
                  class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white py-1.5 px-3 rounded text-sm transition-colors mb-2"
                >
                  {{ synergist.loadingLookupPropertiesRaw ? '...' : 'Fetch Raw Metadata' }}
                </button>
                <div v-if="synergist.lookupPropertiesRawError" class="text-xs text-red-400 mb-2">
                  {{ synergist.lookupPropertiesRawError }}
                </div>
                <div v-if="synergist.lookupPropertiesRawModel.length" class="mb-2">
                  <div class="text-xs text-gray-400 mb-1">Available metadata columns:</div>
                  <div class="bg-gray-800 rounded-lg p-2 flex flex-wrap gap-1">
                    <span
                      v-for="col in synergist.lookupPropertiesRawModel"
                      :key="col.data"
                      class="text-[11px] font-mono px-1.5 py-0.5 rounded"
                      :class="col.editable ? 'bg-green-900/30 text-green-400' : 'bg-gray-700 text-gray-400'"
                    >{{ col.data }}</span>
                  </div>
                </div>
                <div v-if="synergist.lookupPropertiesRawData.length" class="bg-gray-800 rounded-lg max-h-80 overflow-y-auto">
                  <div class="text-xs text-gray-400 p-2 border-b border-gray-700">
                    {{ synergist.lookupPropertiesRawData.length }} custom field properties found
                  </div>
                  <div
                    v-for="(prop, idx) in synergist.lookupPropertiesRawData"
                    :key="idx"
                    class="p-2 border-b border-gray-700 last:border-0"
                    :class="{ 'bg-amber-900/15': /phase/i.test(JSON.stringify(prop)) }"
                  >
                    <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                      <template v-for="(val, key) in prop" :key="key">
                        <span class="text-[11px] font-mono text-gray-500">{{ key }}</span>
                        <span class="text-xs text-gray-200 break-all">{{ val === '' || val === null ? '(empty)' : val }}</span>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- LOGIN RESTRICTIONS -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <button
            @click="toggleSection('loginRestriction')"
            class="w-full flex items-center justify-between py-3 px-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('loginRestriction') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-gray-200 uppercase tracking-wide">Login Restrictions</span>
            </div>
            <span
              :class="loginRestriction.enabled ? 'bg-yellow-900 text-yellow-300' : 'bg-gray-700 text-gray-400'"
              class="text-xs px-2 py-0.5 rounded-full font-medium"
            >
              {{ loginRestriction.enabled ? 'Restricting' : 'Open' }}
            </span>
          </button>

          <div v-show="openSections.has('loginRestriction')" class="mt-3 pl-2 space-y-3">

            <!-- Loading -->
            <div v-if="loginRestriction.loading" class="text-xs text-gray-500">Loading...</div>

            <template v-else>
              <!-- Env var info -->
              <div class="bg-gray-800 rounded-lg p-3">
                <div class="flex justify-between items-center mb-2">
                  <span class="text-xs text-gray-500 uppercase tracking-wide">Env var (ALLOWED_LOGIN_EMAILS)</span>
                  <span :class="loginRestriction.envConfigured ? 'text-yellow-400' : 'text-gray-500'" class="text-xs">
                    {{ loginRestriction.envConfigured ? 'Set' : 'Not set' }}
                  </span>
                </div>
                <div v-if="loginRestriction.envConfigured" class="text-xs text-gray-400">
                  {{ loginRestriction.envEmails.join(', ') }}
                </div>
                <div v-else class="text-xs text-gray-500 italic">
                  No env var restriction — login open to all @weareowen.com
                </div>
              </div>

              <!-- Toggle -->
              <div class="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                <div>
                  <span class="text-sm text-gray-200">Restrict logins</span>
                  <p class="text-xs text-gray-500 mt-0.5">When enabled, only listed emails can log in</p>
                </div>
                <button
                  @click="toggleLoginRestriction"
                  :disabled="loginRestriction.saving"
                  :class="loginRestriction.enabled ? 'bg-yellow-600' : 'bg-gray-600'"
                  class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50"
                >
                  <span
                    :class="loginRestriction.enabled ? 'translate-x-6' : 'translate-x-1'"
                    class="inline-block h-4 w-4 rounded-full bg-white transition-transform"
                  />
                </button>
              </div>

              <!-- Allowed emails list -->
              <div class="bg-gray-800 rounded-lg p-3">
                <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Allowed Emails</h4>
                <p class="text-xs text-gray-500 mb-2">
                  {{ loginRestriction.allowedEmails.length > 0 ? 'These emails can log in when restriction is enabled:' : 'Using env var list (or add custom emails below):' }}
                </p>

                <!-- Email chips -->
                <div class="flex flex-wrap gap-1.5 mb-2">
                  <span
                    v-for="(email, idx) in loginRestriction.allowedEmails"
                    :key="idx"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-700 text-gray-300"
                  >
                    {{ email }}
                    <button @click="removeAllowedEmail(idx)" class="text-gray-500 hover:text-red-400 ml-0.5">&times;</button>
                  </span>
                  <span v-if="loginRestriction.allowedEmails.length === 0" class="text-xs text-gray-600 italic">
                    Falls back to env var list
                  </span>
                </div>

                <!-- Add email -->
                <form @submit.prevent="addAllowedEmail" class="flex gap-2">
                  <input
                    v-model="loginRestriction.newEmail"
                    type="email"
                    placeholder="email@weareowen.com"
                    class="flex-1 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                  />
                  <button
                    type="submit"
                    :disabled="!loginRestriction.newEmail || loginRestriction.saving"
                    class="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium rounded transition-colors"
                  >
                    Add
                  </button>
                </form>
              </div>

              <!-- Save status -->
              <div v-if="loginRestriction.error" class="p-2 rounded text-xs bg-red-900/50 text-red-300">
                {{ loginRestriction.error }}
              </div>
              <div v-if="loginRestriction.saved" class="p-2 rounded text-xs bg-green-900/50 text-green-300">
                Saved
              </div>
            </template>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- SECTION 2: BASECAMP -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <button
            @click="toggleSection('basecamp')"
            class="w-full flex items-center justify-between py-3 px-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('basecamp') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-gray-200 uppercase tracking-wide">Basecamp</span>
            </div>
            <span
              :class="externalServices.basecamp.oauthConnected ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'"
              class="text-xs px-2 py-0.5 rounded-full font-medium"
            >
              {{ externalServices.basecamp.oauthConnected ? 'Connected' : 'Not Connected' }}
            </span>
          </button>

          <div v-show="openSections.has('basecamp')" class="mt-3 pl-2 space-y-3">
            <!-- Status -->
            <div class="bg-gray-800 rounded-lg p-3">
              <div class="grid grid-cols-2 gap-x-6 gap-y-2">
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-400">App Credentials</span>
                  <span :class="externalServices.basecamp.hasClientId ? 'text-green-400' : 'text-red-400'" class="text-sm font-medium">
                    {{ externalServices.basecamp.hasClientId ? 'Configured' : 'Awaiting IT' }}
                  </span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-400">Your Account</span>
                  <span :class="externalServices.basecamp.oauthConnected ? 'text-green-400' : 'text-yellow-400'" class="text-sm font-medium">
                    {{ externalServices.basecamp.oauthConnected ? 'Connected' : 'Not connected' }}
                  </span>
                </div>
              </div>
              <div v-if="externalServices.basecamp.oauthConnected" class="mt-2 pt-2 border-t border-gray-700">
                <div v-if="externalServices.basecamp.identity" class="text-xs text-gray-400">User: <span class="text-gray-300">{{ externalServices.basecamp.identity }}</span></div>
                <div v-if="externalServices.basecamp.accounts?.length" class="text-xs text-gray-400 mt-1">
                  Accounts ({{ externalServices.basecamp.accounts.length }}):
                  <div v-for="acct in externalServices.basecamp.accounts" :key="acct.accountId" class="ml-2 mt-0.5 text-gray-300">
                    {{ acct.accountName }} <code class="text-indigo-400 bg-gray-700 px-1 rounded text-[10px] select-all">{{ acct.accountId }}</code>
                  </div>
                </div>
              </div>
            </div>

            <!-- Connect / Disconnect Basecamp -->
            <button
              v-if="!externalServices.basecamp.oauthConnected && externalServices.basecamp.hasClientId"
              @click="connectBasecamp"
              :disabled="basecamp.connecting"
              class="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
            >
              {{ basecamp.connecting ? 'Connecting...' : 'Connect your Basecamp Account' }}
            </button>
            <button
              v-if="externalServices.basecamp.oauthConnected"
              @click="disconnectBasecamp"
              :disabled="basecamp.disconnectingOAuth"
              class="w-full bg-gray-700 hover:bg-red-900 text-gray-300 hover:text-red-300 py-2 px-4 rounded-lg text-sm font-medium transition-colors"
            >
              {{ basecamp.disconnectingOAuth ? 'Disconnecting...' : 'Disconnect Basecamp' }}
            </button>

            <!-- Test Connection -->
            <button
              @click="testBasecamp"
              :disabled="basecamp.testing"
              class="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
            >
              <span v-if="basecamp.testing">Testing...</span>
              <span v-else>Test Connection</span>
            </button>

            <div v-if="basecamp.result" class="p-3 rounded-lg text-sm" :class="basecamp.result.connected ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'">
              <div class="font-medium mb-1">
                {{ basecamp.result.connected ? 'Connected' : 'Connection Failed' }}
              </div>
              <div v-if="basecamp.result.error" class="text-xs opacity-75">{{ basecamp.result.error }}</div>
              <div v-if="basecamp.result.identity" class="text-xs opacity-75">Identity: {{ basecamp.result.identity }}</div>
            </div>

            <!-- Test Full Setup -->
            <div class="pt-3 border-t border-gray-700">
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Test Operations</h4>
              <button
                @click="testBasecampFullSetup"
                :disabled="basecamp.testingSetup"
                class="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-4 rounded-lg text-sm transition-colors text-left"
              >
                <span v-if="basecamp.testingSetup">Creating...</span>
                <span v-else>Test: Create Project + Todolist + Tasks</span>
              </button>
              <div v-if="basecamp.setupResult" class="mt-3 bg-gray-800 rounded-lg p-3 max-h-40 overflow-y-auto">
                <div class="text-xs text-gray-400 mb-2">Result:</div>
                <div class="text-xs text-gray-300 space-y-1">
                  <div>Project ID: <span class="text-indigo-400">{{ basecamp.setupResult.project?.id }}</span></div>
                  <div>Todolist ID: <span class="text-indigo-400">{{ basecamp.setupResult.todolist?.id }}</span></div>
                  <div>Tasks: <span class="text-indigo-400">{{ basecamp.setupResult.todos?.length || 0 }}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- SECTION 3: GOOGLE DRIVE -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <button
            @click="toggleSection('drive')"
            class="w-full flex items-center justify-between py-3 px-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('drive') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-gray-200 uppercase tracking-wide">Google Drive</span>
            </div>
            <span
              :class="externalServices.googleDrive.userConnected ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'"
              class="text-xs px-2 py-0.5 rounded-full font-medium"
            >
              {{ externalServices.googleDrive.userConnected ? 'Connected' : 'Not Connected' }}
            </span>
          </button>

          <div v-show="openSections.has('drive')" class="mt-3 pl-2 space-y-4">

            <!-- Sub-section A: Connection & Status -->
            <div>
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Connection & Status</h4>
              <div class="bg-gray-800 rounded-lg p-3 mb-3">
                <div class="grid grid-cols-2 gap-x-6 gap-y-2">
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-gray-400">Your Account</span>
                    <span :class="externalServices.googleDrive.userConnected ? 'text-green-400' : 'text-red-400'" class="text-sm font-medium">
                      {{ externalServices.googleDrive.userConnected ? 'Connected (via Google login)' : 'Not connected' }}
                    </span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-gray-400">Service Account (fallback)</span>
                    <span :class="externalServices.googleDrive.hasKeyFile ? 'text-green-400' : 'text-gray-500'" class="text-sm font-medium">
                      {{ externalServices.googleDrive.hasKeyFile ? 'Configured' : 'Not configured' }}
                    </span>
                  </div>
                </div>
              </div>

              <button
                @click="testDrive"
                :disabled="drive.testing"
                class="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
              >
                <span v-if="drive.testing">Testing...</span>
                <span v-else>Test Connection</span>
              </button>

              <div v-if="drive.result" class="mt-3 p-3 rounded-lg text-sm" :class="drive.result.connected ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'">
                <div class="font-medium mb-1">
                  {{ drive.result.connected ? 'Connected' : 'Connection Failed' }}
                </div>
                <div v-if="drive.result.error" class="text-xs opacity-75">{{ drive.result.error }}</div>
                <div v-if="drive.result.sharedDrive" class="text-xs opacity-75">Shared Drive: {{ drive.result.sharedDrive.name }}</div>
              </div>
            </div>

            <!-- Sub-section B: Discovery (.env values) -->
            <div class="pt-3 border-t border-gray-700">
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Discovery <span class="text-yellow-400 ml-1">ENV</span>
              </h4>

              <!-- Shared Drive Discovery -->
              <div class="mb-4">
                <div class="text-xs text-gray-400 mb-1">Shared Drive ID</div>
                <div v-if="drive.sharedDriveId" class="bg-green-900/30 border border-green-700 rounded-lg p-2 mb-2">
                  <div class="text-xs text-green-400 mb-1">GOOGLE_SHARED_DRIVE_ID (.env):</div>
                  <div class="text-xs text-green-300 font-mono select-all">{{ drive.sharedDriveId }}</div>
                </div>
                <div v-else class="bg-yellow-900/30 border border-yellow-700 rounded-lg p-2 mb-2">
                  <div class="text-xs text-yellow-400">GOOGLE_SHARED_DRIVE_ID not set in .env</div>
                </div>

                <button
                  @click="fetchSharedDrives"
                  :disabled="drive.loadingSharedDrives"
                  class="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-4 rounded-lg text-sm transition-colors text-left mb-2"
                >
                  <span v-if="drive.loadingSharedDrives">Loading...</span>
                  <span v-else>Discover Shared Drives</span>
                </button>

                <div v-if="drive.sharedDrives.length" class="bg-gray-800 rounded-lg max-h-32 overflow-y-auto">
                  <div class="p-2 text-xs text-gray-400 border-b border-gray-700">
                    Copy the ID → add to .env as GOOGLE_SHARED_DRIVE_ID
                  </div>
                  <div
                    v-for="driveItem in drive.sharedDrives"
                    :key="driveItem.id"
                    class="p-2 border-b border-gray-700 last:border-0"
                  >
                    <div class="text-sm text-gray-200">{{ driveItem.name }}</div>
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-indigo-400 font-mono select-all cursor-text flex-1">{{ driveItem.id }}</span>
                      <button
                        @click="copyToClipboard(driveItem.id)"
                        class="text-gray-400 hover:text-white p-1"
                        title="Copy to clipboard"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Client Folder Navigator -->
              <div v-if="drive.sharedDriveId" class="mb-2">
                <div class="text-xs text-gray-400 mb-1">Client Folder (GOOGLE_DRIVE_CLIENT_FOLDER_ID)</div>
                <p class="text-xs text-gray-500 mb-2">Navigate to find client parent folders (can be nested deep)</p>

                <div v-if="drive.selectedClientFolder" class="bg-green-900/30 border border-green-700 rounded-lg p-2 mb-2">
                  <div class="text-xs text-green-400 mb-1">Selected Client Folder:</div>
                  <div class="text-sm text-green-300 font-medium">{{ drive.selectedClientFolder.name }}</div>
                  <div class="flex items-center gap-2 mt-1">
                    <span class="text-xs text-indigo-400 font-mono select-all flex-1">{{ drive.selectedClientFolder.id }}</span>
                    <button
                      @click="copyToClipboard(drive.selectedClientFolder.id)"
                      class="text-gray-400 hover:text-white p-1"
                      title="Copy to clipboard"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                  <div class="text-xs text-gray-500 mt-1">Copy ID → add to .env as GOOGLE_DRIVE_CLIENT_FOLDER_ID</div>
                  <button
                    @click="navClearSelection"
                    class="mt-2 text-xs text-gray-400 hover:text-gray-300"
                  >
                    Clear Selection
                  </button>
                </div>

                <button
                  v-if="!drive.navPath.length"
                  @click="startFolderNav"
                  class="w-full bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm transition-colors text-left mb-2"
                >
                  Browse Folder Structure
                </button>

                <!-- Navigator Active -->
                <div v-if="drive.navPath.length" class="bg-gray-800 rounded-lg p-2 mb-2">
                  <div class="flex items-center gap-1 text-xs mb-2 flex-wrap">
                    <span
                      v-for="(crumb, index) in drive.navPath"
                      :key="crumb.id"
                      class="flex items-center"
                    >
                      <button
                        v-if="index < drive.navPath.length - 1"
                        @click="navGoToLevel(index)"
                        class="text-indigo-400 hover:text-indigo-300"
                      >
                        {{ crumb.name }}
                      </button>
                      <span v-else class="text-gray-300">{{ crumb.name }}</span>
                      <span v-if="index < drive.navPath.length - 1" class="text-gray-500 mx-1">/</span>
                    </span>
                  </div>

                  <button
                    v-if="drive.navPath.length > 1"
                    @click="navGoUp"
                    :disabled="drive.loadingNav"
                    class="text-xs text-gray-400 hover:text-white mb-2 flex items-center gap-1"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                    </svg>
                    Up
                  </button>

                  <div v-if="drive.loadingNav" class="text-xs text-gray-400 py-2">Loading...</div>
                  <div v-else-if="drive.navFolders.length" class="max-h-32 overflow-y-auto">
                    <div
                      v-for="folder in drive.navFolders"
                      :key="folder.id"
                      class="flex items-center justify-between py-1 border-b border-gray-700 last:border-0"
                    >
                      <span class="text-sm text-gray-200 truncate flex-1">{{ folder.name }}</span>
                      <div class="flex gap-1 ml-2">
                        <button
                          @click="navOpenFolder(folder)"
                          class="text-xs bg-gray-700 hover:bg-gray-600 text-white py-1 px-2 rounded"
                        >
                          Open
                        </button>
                        <button
                          @click="navSelectFolder(folder)"
                          class="text-xs bg-indigo-600 hover:bg-indigo-700 text-white py-1 px-2 rounded"
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-xs text-gray-500 py-2">No subfolders</div>

                  <button
                    @click="closeFolderNav"
                    class="mt-2 text-xs text-gray-400 hover:text-gray-300"
                  >
                    Close Navigator
                  </button>
                </div>
              </div>
            </div>

            <!-- Sub-section C: Configuration -->
            <div class="pt-3 border-t border-gray-700">
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Folder Structure Config</h4>

              <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <!-- Phase Subfolders -->
                <div>
                  <label class="text-xs text-gray-400 mb-1 block">Phase Subfolders (Trigger 1)</label>
                  <div class="flex flex-wrap gap-1 mb-2">
                    <span
                      v-for="(subfolder, index) in drive.folderConfig.phaseSubfolders"
                      :key="subfolder"
                      class="inline-flex items-center bg-gray-700 text-gray-200 text-xs px-2 py-1 rounded"
                    >
                      {{ subfolder }}
                      <button @click="removePhaseSubfolder(index)" class="ml-1 text-gray-400 hover:text-red-400">&times;</button>
                    </span>
                    <span v-if="!drive.folderConfig.phaseSubfolders.length" class="text-xs text-gray-500 italic">None</span>
                  </div>
                  <div class="flex gap-1">
                    <input
                      v-model="drive.folderConfig.newPhaseSubfolder"
                      @keyup.enter="addPhaseSubfolder"
                      type="text"
                      placeholder="Add..."
                      class="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200 focus:border-indigo-500 focus:outline-none"
                    />
                    <button
                      @click="addPhaseSubfolder"
                      :disabled="!drive.folderConfig.newPhaseSubfolder.trim()"
                      class="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-700 text-white text-xs px-2 py-1 rounded"
                    >Add</button>
                  </div>
                </div>

                <!-- Deliverable Subfolders -->
                <div>
                  <label class="text-xs text-gray-400 mb-1 block">Deliverable Subfolders (Trigger 2)</label>
                  <div class="flex flex-wrap gap-1 mb-2">
                    <span
                      v-for="(subfolder, index) in drive.folderConfig.deliverableSubfolders"
                      :key="subfolder"
                      class="inline-flex items-center bg-gray-700 text-gray-200 text-xs px-2 py-1 rounded"
                    >
                      {{ subfolder }}
                      <button @click="removeDeliverableSubfolder(index)" class="ml-1 text-gray-400 hover:text-red-400">&times;</button>
                    </span>
                    <span v-if="!drive.folderConfig.deliverableSubfolders.length" class="text-xs text-gray-500 italic">None</span>
                  </div>
                  <div class="flex gap-1">
                    <input
                      v-model="drive.folderConfig.newDeliverableSubfolder"
                      @keyup.enter="addDeliverableSubfolder"
                      type="text"
                      placeholder="Add..."
                      class="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200 focus:border-indigo-500 focus:outline-none"
                    />
                    <button
                      @click="addDeliverableSubfolder"
                      :disabled="!drive.folderConfig.newDeliverableSubfolder.trim()"
                      class="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-700 text-white text-xs px-2 py-1 rounded"
                    >Add</button>
                  </div>
                </div>

                <!-- Job Subfolders -->
                <div>
                  <label class="text-xs text-gray-400 mb-1 block">Job Subfolders</label>
                  <div class="flex flex-wrap gap-1 mb-2">
                    <span
                      v-for="(subfolder, index) in drive.folderConfig.jobSubfolders"
                      :key="subfolder"
                      class="inline-flex items-center bg-gray-700 text-gray-200 text-xs px-2 py-1 rounded"
                    >
                      {{ subfolder }}
                      <button @click="removeJobSubfolder(index)" class="ml-1 text-gray-400 hover:text-red-400">&times;</button>
                    </span>
                    <span v-if="!drive.folderConfig.jobSubfolders.length" class="text-xs text-gray-500 italic">None (default: Studio)</span>
                  </div>
                  <div class="flex gap-1">
                    <input
                      v-model="drive.folderConfig.newJobSubfolder"
                      @keyup.enter="addJobSubfolder"
                      type="text"
                      placeholder="Add..."
                      class="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200 focus:border-indigo-500 focus:outline-none"
                    />
                    <button
                      @click="addJobSubfolder"
                      :disabled="!drive.folderConfig.newJobSubfolder.trim()"
                      class="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-700 text-white text-xs px-2 py-1 rounded"
                    >Add</button>
                  </div>
                </div>
              </div>

              <div v-if="drive.folderConfig.saving" class="mt-2 text-xs text-yellow-400">Saving...</div>
            </div>

            <!-- Sub-section D: Test Operations -->
            <div class="pt-3 border-t border-gray-700">
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Test Operations</h4>
              <button
                @click="testDriveHierarchy"
                :disabled="drive.testingHierarchy"
                class="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-4 rounded-lg text-sm transition-colors text-left"
              >
                <span v-if="drive.testingHierarchy">Creating...</span>
                <span v-else>Test: Create Phase Folder Hierarchy</span>
              </button>

              <div v-if="drive.hierarchyResult" class="mt-3 bg-gray-800 rounded-lg p-3 max-h-40 overflow-y-auto">
                <div class="text-xs text-gray-400 mb-2">Result:</div>
                <div class="text-xs text-gray-300 space-y-1">
                  <div>Client: <span class="text-indigo-400">{{ drive.hierarchyResult.clientFolder?.name }}</span></div>
                  <div v-if="drive.hierarchyResult.jobFolder">Job: <span class="text-indigo-400">{{ drive.hierarchyResult.jobFolder?.name }}</span></div>
                  <div>Phase: <span class="text-indigo-400">{{ drive.hierarchyResult.phaseFolder?.name }}</span></div>
                </div>
              </div>
            </div>

            <!-- Sub-section: Knowledge Base -->
            <div>
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Knowledge Base</h4>

              <button
                @click="refreshKbCache"
                :disabled="kb.refreshing"
                class="w-full mb-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors text-left"
                title="Forces a fresh Drive walk + re-reads of all cached files. Use after editing or adding KB files so the Hub sees the changes before the TTL expires (listing=24h, content=5min)."
              >
                <span v-if="kb.refreshing">Refreshing...</span>
                <span v-else>Refresh KB Cache</span>
              </button>

              <div v-if="kb.refreshError" class="mb-2 p-2 bg-red-900/50 text-red-300 rounded text-xs">{{ kb.refreshError }}</div>
              <div v-else-if="kb.refreshedAt" class="mb-2 p-2 bg-emerald-900/40 text-emerald-300 rounded text-xs">Cache cleared at {{ new Date(kb.refreshedAt).toLocaleTimeString() }} — next request re-walks Drive.</div>

              <button
                @click="listKbFiles"
                :disabled="kb.listing"
                class="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-4 rounded-lg text-sm transition-colors text-left"
              >
                <span v-if="kb.listing">Listing...</span>
                <span v-else>List KB Files (Drive)</span>
              </button>

              <div v-if="kb.listError" class="mt-2 p-2 bg-red-900/50 text-red-300 rounded text-xs">{{ kb.listError }}</div>

              <div v-if="kb.files.length" class="mt-3 bg-gray-800 rounded-lg p-3 max-h-60 overflow-y-auto">
                <div class="text-xs text-gray-400 mb-2">{{ kb.files.length }} files found:</div>
                <div class="space-y-1">
                  <div
                    v-for="file in kb.files" :key="file.id"
                    class="flex items-center justify-between text-xs"
                  >
                    <span class="text-gray-300 font-mono truncate mr-2">{{ file.relativePath }}</span>
                    <button
                      @click="readKbFile(file)"
                      class="text-indigo-400 hover:text-indigo-300 whitespace-nowrap"
                    >Read</button>
                  </div>
                </div>
              </div>

              <button
                @click="assembleKbContext"
                :disabled="kb.assembling"
                class="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors text-left"
              >
                <span v-if="kb.assembling">Assembling...</span>
                <span v-else>Assemble KB Context (brief-to-estimation)</span>
              </button>

              <div v-if="kb.assembleError" class="mt-2 p-2 bg-red-900/50 text-red-300 rounded text-xs">{{ kb.assembleError }}</div>

              <div v-if="kb.assembleResult" class="mt-3 bg-gray-800 rounded-lg p-3 max-h-60 overflow-y-auto">
                <div class="text-xs text-gray-400 mb-2">Context assembled for: <span class="text-indigo-400">{{ kb.assembleResult.sopName }}</span></div>
                <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
                  <div class="text-gray-400">Files loaded:</div>
                  <div class="text-gray-200">{{ kb.assembleResult.fileCount }}</div>
                  <div class="text-gray-400">SOP tokens:</div>
                  <div class="text-gray-200">~{{ kb.assembleResult.sopTokens?.toLocaleString() }}</div>
                  <div class="text-gray-400">Reference tokens:</div>
                  <div class="text-gray-200">~{{ kb.assembleResult.fileTokens?.toLocaleString() }}</div>
                  <div class="text-gray-400">Total system prompt:</div>
                  <div class="text-yellow-300 font-medium">~{{ kb.assembleResult.totalTokens?.toLocaleString() }} tokens</div>
                  <div class="text-gray-400">Cache:</div>
                  <div class="text-gray-200">{{ kb.assembleResult.cacheStats?.fresh }} fresh / {{ kb.assembleResult.cacheStats?.stale }} stale</div>
                </div>
                <div class="text-xs text-gray-500 mb-1">Files:</div>
                <div v-for="file in kb.assembleResult.files" :key="file.path" class="text-xs text-gray-300 font-mono">
                  {{ file.path }} <span class="text-gray-500">(~{{ file.tokens }} tok)</span>
                </div>
              </div>

              <button
                @click="testEstimation"
                :disabled="kb.estimating"
                class="w-full mt-2 bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors text-left"
              >
                <span v-if="kb.estimating">Streaming...</span>
                <span v-else>Test Estimation (hardcoded brief → Claude)</span>
              </button>

              <div v-if="kb.estimateError" class="mt-2 p-2 bg-red-900/50 text-red-300 rounded text-xs">{{ kb.estimateError }}</div>

              <div v-if="kb.estimateResult !== null" class="mt-3 bg-gray-800 rounded-lg p-3">
                <div class="flex justify-between items-center mb-2">
                  <span class="text-xs text-gray-400">Claude response <span v-if="kb.estimating" class="text-yellow-400">(streaming...)</span></span>
                  <span v-if="kb.estimateUsage" class="text-xs text-gray-500">{{ kb.estimateUsage.input_tokens?.toLocaleString() }} in / {{ kb.estimateUsage.output_tokens?.toLocaleString() }} out</span>
                </div>
                <pre class="text-xs text-gray-300 max-h-64 overflow-y-auto whitespace-pre-wrap">{{ kb.estimateResult }}</pre>
              </div>

              <div v-if="kb.readResult" class="mt-3 bg-gray-800 rounded-lg p-3">
                <div class="flex justify-between items-center mb-2">
                  <span class="text-xs text-gray-400 font-mono">{{ kb.readResult.path }}</span>
                  <span class="text-xs text-gray-500">{{ kb.readResult.contentLength }} chars</span>
                </div>
                <pre class="text-xs text-gray-300 max-h-48 overflow-y-auto whitespace-pre-wrap">{{ kb.readResult.preview }}</pre>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- SECTION 4: GOOGLE SPACES -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <button
            @click="toggleSection('spaces')"
            class="w-full flex items-center justify-between py-3 px-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('spaces') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-gray-200 uppercase tracking-wide">Google Spaces</span>
            </div>
            <span
              :class="externalServices.googleSpaces.userConnected ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'"
              class="text-xs px-2 py-0.5 rounded-full font-medium"
            >
              {{ externalServices.googleSpaces.userConnected ? 'Connected' : 'Not Connected' }}
            </span>
          </button>

          <div v-show="openSections.has('spaces')" class="mt-3 pl-2 space-y-3">
            <div class="bg-gray-800 rounded-lg p-3">
              <div class="grid grid-cols-2 gap-x-6 gap-y-2">
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-400">Your Account</span>
                  <span :class="externalServices.googleSpaces.userConnected ? 'text-green-400' : 'text-red-400'" class="text-sm font-medium">
                    {{ externalServices.googleSpaces.userConnected ? 'Connected (via Google login)' : 'Not connected' }}
                  </span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-400">Service Account (fallback)</span>
                  <span :class="externalServices.googleSpaces.hasKeyFile ? 'text-green-400' : 'text-gray-500'" class="text-sm font-medium">
                    {{ externalServices.googleSpaces.hasKeyFile ? 'Configured' : 'Not configured' }}
                  </span>
                </div>
              </div>
            </div>

            <button
              @click="testSpaces"
              :disabled="spaces.testing"
              class="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
            >
              <span v-if="spaces.testing">Testing...</span>
              <span v-else>Test Connection</span>
            </button>

            <div v-if="spaces.result" class="p-3 rounded-lg text-sm" :class="spaces.result.connected ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'">
              <div class="font-medium mb-1">
                {{ spaces.result.connected ? 'Connected' : 'Connection Failed' }}
              </div>
              <div v-if="spaces.result.error" class="text-xs opacity-75">{{ spaces.result.error }}</div>
              <div v-if="spaces.result.spacesCount !== undefined" class="text-xs opacity-75">Spaces: {{ spaces.result.spacesCount }}</div>
            </div>

            <div class="pt-3 border-t border-gray-700">
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Test Operations</h4>

              <button
                @click="testSpacesCreate"
                :disabled="spaces.testingCreate"
                class="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-4 rounded-lg text-sm transition-colors text-left mb-2"
              >
                <span v-if="spaces.testingCreate">Creating...</span>
                <span v-else>Test: Create Space (no members)</span>
              </button>

              <div v-if="spaces.createResult" class="mb-3 p-3 rounded-lg text-sm" :class="spaces.createResult.error ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'">
                <div class="font-medium mb-1">
                  {{ spaces.createResult.error ? 'Create Failed' : 'Space Created' }}
                </div>
                <div v-if="spaces.createResult.error" class="text-xs opacity-75">{{ spaces.createResult.error }}</div>
                <div v-if="spaces.createResult.name" class="text-xs opacity-75 mt-1">Name: {{ spaces.createResult.displayName }}</div>
                <div v-if="spaces.createResult.spaceUrl" class="text-xs opacity-75">
                  <a :href="spaces.createResult.spaceUrl" target="_blank" class="text-blue-400 hover:text-blue-300 underline">Open Space</a>
                </div>
              </div>

              <button
                @click="testSpacesFullSetup"
                :disabled="spaces.testingSetup"
                class="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white py-2 px-4 rounded-lg text-sm transition-colors text-left"
              >
                <span v-if="spaces.testingSetup">Creating...</span>
                <span v-else>Test: Create Space + Welcome Message</span>
              </button>

              <div v-if="spaces.setupResult" class="mt-3 bg-gray-800 rounded-lg p-3 max-h-40 overflow-y-auto">
                <div class="text-xs text-gray-400 mb-2">Result:</div>
                <div class="text-xs text-gray-300 space-y-1">
                  <div>Space: <span class="text-indigo-400">{{ spaces.setupResult.space?.displayName }}</span></div>
                  <div>ID: <span class="text-indigo-400">{{ spaces.setupResult.space?.name }}</span></div>
                  <div v-if="spaces.setupResult.message">Message: <span class="text-green-400">Sent</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- SECTION 5: INTEGRATION TESTS -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <button
            @click="toggleSection('integration')"
            class="w-full flex items-center justify-between py-3 px-3 bg-indigo-900/40 border border-indigo-700 rounded-lg hover:bg-indigo-900/60 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-indigo-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('integration') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-indigo-300 uppercase tracking-wide">Integration Tests</span>
            </div>
          </button>

          <div v-show="openSections.has('integration')" class="mt-3 pl-2 space-y-3">
            <p class="text-xs text-gray-400">Tests all three services together: Basecamp project, Drive folders, Google Space</p>
            <button
              @click="testTrigger1"
              :disabled="trigger1.testing"
              class="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
            >
              <span v-if="trigger1.testing">Running...</span>
              <span v-else>Run Full Trigger 1 Test</span>
            </button>

            <div v-if="trigger1.result" class="mt-3">
              <div class="space-y-2">
                <div class="flex items-center gap-2">
                  <span :class="trigger1.result.basecamp ? 'text-green-400' : 'text-red-400'">{{ trigger1.result.basecamp ? '✓' : '✗' }}</span>
                  <span class="text-xs text-gray-300">Basecamp</span>
                </div>
                <div class="flex items-center gap-2">
                  <span :class="trigger1.result.drive ? 'text-green-400' : 'text-red-400'">{{ trigger1.result.drive ? '✓' : '✗' }}</span>
                  <span class="text-xs text-gray-300">Drive</span>
                </div>
                <div class="flex items-center gap-2">
                  <span :class="trigger1.result.spaces ? 'text-green-400' : 'text-red-400'">{{ trigger1.result.spaces ? '✓' : '✗' }}</span>
                  <span class="text-xs text-gray-300">Spaces</span>
                </div>
              </div>
              <div v-if="trigger1.result.errors?.length" class="mt-2 text-xs text-red-400">
                Errors: {{ trigger1.result.errors.map(e => e.service + ': ' + e.error).join(', ') }}
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- SECTION 6: ENVIRONMENT -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <button
            @click="toggleSection('environment')"
            class="w-full flex items-center justify-between py-3 px-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('environment') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-gray-200 uppercase tracking-wide">Environment</span>
            </div>
          </button>

          <div v-show="openSections.has('environment')" class="mt-3 pl-2">
            <div class="text-xs text-gray-400 space-y-1">
              <div>API: {{ apiUrl }}</div>
              <div>Build: Development</div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- SECTION: DATABASE -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <button
            @click="onDatabaseToggle"
            class="w-full flex items-center justify-between py-3 px-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('database') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-gray-200 uppercase tracking-wide">Database</span>
            </div>
            <span
              v-if="dbInfo.data"
              :class="dbInfo.data.readyState === 1 ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'"
              class="text-xs px-2 py-0.5 rounded-full font-medium"
            >
              {{ dbInfo.data.readyState === 1 ? 'Connected' : 'Disconnected' }}
            </span>
          </button>

          <div v-show="openSections.has('database')" class="mt-3 pl-2">
            <div v-if="dbInfo.loading" class="text-xs text-gray-500 py-2">Loading…</div>
            <div v-else-if="dbInfo.error" class="text-xs text-red-400 py-2">{{ dbInfo.error }}</div>
            <div v-else-if="dbInfo.data" class="space-y-3">

              <!-- Runtime connection -->
              <div>
                <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Runtime Connection</h4>
                <div class="bg-gray-800 rounded-lg p-3 space-y-2 text-xs">
                  <div class="flex justify-between items-center">
                    <span class="text-gray-400">Connected DB</span>
                    <span class="text-green-400 font-mono">{{ dbInfo.data.connectedDatabase || '(none)' }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-gray-400">Host</span>
                    <span class="text-gray-300 font-mono text-right truncate ml-3">{{ dbInfo.data.connectedHost || '(none)' }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-gray-400">Ready state</span>
                    <span :class="dbInfo.data.readyState === 1 ? 'text-green-400' : 'text-yellow-400'" class="font-mono">
                      {{ dbInfo.data.readyState }} ({{ readyStateLabel(dbInfo.data.readyState) }})
                    </span>
                  </div>
                </div>
              </div>

              <!-- Connection-string components -->
              <div>
                <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">DATABASE_URL Breakdown <span class="text-gray-600 normal-case">(credentials stripped)</span></h4>
                <div class="bg-gray-800 rounded-lg p-3 space-y-2 text-xs">
                  <div class="flex justify-between items-center">
                    <span class="text-gray-400">URL host</span>
                    <span class="text-gray-300 font-mono text-right truncate ml-3">{{ dbInfo.data.mongodbUriHost || '(none)' }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-gray-400">URL path</span>
                    <span :class="dbInfo.data.mongodbUriPath && dbInfo.data.mongodbUriPath !== '/' ? 'text-green-400' : 'text-yellow-400'" class="font-mono">
                      {{ dbInfo.data.mongodbUriPath || '(empty)' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Override -->
              <div>
                <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">DB-Name Override</h4>
                <div class="bg-gray-800 rounded-lg p-3 space-y-2 text-xs">
                  <div class="flex justify-between items-center">
                    <span class="text-gray-400">DB_NAME env</span>
                    <span :class="dbInfo.data.mongodbDbNameEnv ? 'text-green-400' : 'text-yellow-400'" class="font-mono">
                      {{ dbInfo.data.mongodbDbNameEnv || '(unset)' }}
                    </span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-gray-400">Mongoose dbName option applied</span>
                    <span :class="dbInfo.data.usingDbNameOverride ? 'text-green-400' : 'text-gray-500'">
                      {{ dbInfo.data.usingDbNameOverride ? 'yes' : 'no' }}
                    </span>
                  </div>
                </div>
                <p class="text-[11px] text-gray-500 mt-2">
                  DB name source:
                  <span class="text-gray-300">{{ dbNameSource }}</span>
                </p>
              </div>

              <!-- Refresh -->
              <button
                @click="fetchDbInfo"
                :disabled="dbInfo.loading"
                class="w-full px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200 text-xs font-medium rounded transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- SECTION 7: CONNECTION MANAGEMENT (TESTING) -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <button
            @click="toggleSection('connections')"
            class="w-full flex items-center justify-between py-3 px-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('connections') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-gray-200 uppercase tracking-wide">Clear Connections (Testing)</span>
            </div>
          </button>

          <div v-show="openSections.has('connections')" class="mt-3 pl-2 space-y-3">
            <div class="bg-yellow-900/30 border border-yellow-600/50 rounded p-2">
              <p class="text-xs text-yellow-300">⚠️ Testing only: Disconnect services to re-test setup flows</p>
            </div>

            <!-- Phase ID Input -->
            <div>
              <label class="block text-xs text-gray-400 mb-1">Phase ID</label>
              <input
                v-model="connectionMgmt.phaseId"
                type="text"
                placeholder="e.g. 2/TEST9999.005 or 507f1f77bcf86cd799439011"
                class="w-full bg-gray-700 text-gray-100 text-xs rounded px-2 py-1.5 border border-gray-600 focus:border-blue-500 focus:outline-none font-mono"
              />
              <p class="text-xs text-gray-500 mt-1">
                Accepts <strong class="text-green-400">Synergist ID</strong> (e.g. 2/TEST9999.005) from URL
                <br/>
                or <strong class="text-blue-400">MongoDB ObjectId</strong> (24-char hex)
              </p>
            </div>

            <!-- Disconnect Buttons -->
            <div class="grid grid-cols-3 gap-2">
              <button
                @click="disconnectService('basecamp')"
                :disabled="!connectionMgmt.phaseId || connectionMgmt.disconnecting.basecamp"
                class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white py-2 px-3 rounded text-xs transition-colors"
              >
                <span v-if="connectionMgmt.disconnecting.basecamp">...</span>
                <span v-else>Clear Basecamp</span>
              </button>

              <button
                @click="disconnectService('drive')"
                :disabled="!connectionMgmt.phaseId || connectionMgmt.disconnecting.drive"
                class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white py-2 px-3 rounded text-xs transition-colors"
              >
                <span v-if="connectionMgmt.disconnecting.drive">...</span>
                <span v-else>Clear Drive</span>
              </button>

              <button
                @click="disconnectService('spaces')"
                :disabled="!connectionMgmt.phaseId || connectionMgmt.disconnecting.spaces"
                class="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white py-2 px-3 rounded text-xs transition-colors"
              >
                <span v-if="connectionMgmt.disconnecting.spaces">...</span>
                <span v-else>Clear Spaces</span>
              </button>
            </div>

            <!-- Result -->
            <div v-if="connectionMgmt.result" class="p-2 rounded text-xs" :class="connectionMgmt.result.error ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'">
              <div v-if="connectionMgmt.result.error">❌ {{ connectionMgmt.result.error }}</div>
              <div v-else>✅ {{ connectionMgmt.result.message }}</div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- SECTION 8: DISCOVERY INTERVIEWS -->
        <!-- ============================================================ -->
        <div class="mb-3">
          <button
            @click="toggleSection('interviews')"
            class="w-full flex items-center justify-between py-3 px-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-4 h-4 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': openSections.has('interviews') }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
              <span class="text-sm font-medium text-gray-200 uppercase tracking-wide">Discovery Interviews</span>
            </div>
            <span
              v-if="interviews.sessions.length > 0"
              class="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-900 text-indigo-300"
            >
              {{ interviews.sessions.filter(s => s.status === 'complete').length }}/{{ interviews.sessions.length }} complete
            </span>
          </button>

          <div v-show="openSections.has('interviews')" class="mt-3 pl-2 space-y-4">

            <!-- Create new session -->
            <div>
              <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Create Interview Session</h4>
              <div class="space-y-2">
                <input
                  v-model="interviews.newName"
                  type="text"
                  placeholder="Name (e.g. Sarah Chen)"
                  class="w-full bg-gray-700 text-gray-100 text-xs rounded px-2 py-1.5 border border-gray-600 focus:border-indigo-500 focus:outline-none"
                />
                <input
                  v-model="interviews.newRole"
                  type="text"
                  placeholder="Role (e.g. Senior PM — aligned to CV UK)"
                  class="w-full bg-gray-700 text-gray-100 text-xs rounded px-2 py-1.5 border border-gray-600 focus:border-indigo-500 focus:outline-none"
                />
                <input
                  v-model="interviews.newEmail"
                  type="text"
                  placeholder="Email (optional)"
                  class="w-full bg-gray-700 text-gray-100 text-xs rounded px-2 py-1.5 border border-gray-600 focus:border-indigo-500 focus:outline-none"
                />
                <button
                  @click="createInterview"
                  :disabled="!interviews.newName.trim() || interviews.creating"
                  class="bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 disabled:text-gray-500 text-white py-1.5 px-3 rounded text-xs font-medium transition-colors"
                >
                  {{ interviews.creating ? 'Creating...' : 'Create Session' }}
                </button>
              </div>

              <!-- Created URL -->
              <div v-if="interviews.lastCreatedUrl" class="mt-2 p-2 bg-green-900/30 border border-green-600/50 rounded">
                <p class="text-xs text-green-300 mb-1">Session created. Send this link:</p>
                <div class="flex items-center gap-2">
                  <input
                    :value="interviews.lastCreatedUrl"
                    readonly
                    class="flex-1 bg-gray-800 text-green-200 text-xs rounded px-2 py-1 border border-gray-600 font-mono"
                    @click="$event.target.select()"
                  />
                  <button
                    @click="copyInterviewUrl"
                    class="bg-gray-700 hover:bg-gray-600 text-white py-1 px-2 rounded text-xs transition-colors"
                  >
                    {{ interviews.copied ? '✓' : 'Copy' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Sessions list -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide">Sessions</h4>
                <button
                  @click="fetchInterviews"
                  :disabled="interviews.loading"
                  class="text-xs text-indigo-400 hover:text-indigo-300"
                >
                  {{ interviews.loading ? 'Loading...' : 'Refresh' }}
                </button>
              </div>

              <div v-if="interviews.sessions.length === 0" class="text-xs text-gray-500 italic">
                No interview sessions yet.
              </div>

              <div v-else class="space-y-2">
                <div
                  v-for="session in interviews.sessions"
                  :key="session.token"
                  class="bg-gray-800 rounded p-2 flex items-center justify-between gap-2"
                >
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-gray-200 font-medium truncate">{{ session.name || 'Unnamed' }}</span>
                      <span
                        :class="{
                          'bg-gray-700 text-gray-400': session.status === 'pending',
                          'bg-yellow-900 text-yellow-300': session.status === 'in_progress',
                          'bg-green-900 text-green-300': session.status === 'complete'
                        }"
                        class="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                      >
                        {{ session.status }}
                      </span>
                    </div>
                    <div class="text-[10px] text-gray-500 truncate">{{ session.role }}</div>
                  </div>
                  <div class="flex items-center gap-1 flex-shrink-0">
                    <button
                      @click="copySessionUrl(session.token)"
                      class="text-[10px] text-gray-400 hover:text-indigo-300 px-1"
                      title="Copy interview link"
                    >Link</button>
                    <button
                      v-if="session.status === 'in_progress'"
                      @click="forceCompleteInterview(session.token)"
                      :disabled="interviews.forceCompleting === session.token"
                      class="text-[10px] text-yellow-400 hover:text-yellow-300 disabled:text-gray-500 px-1"
                      title="Force-generate report from partial interview"
                    >{{ interviews.forceCompleting === session.token ? '...' : 'Complete' }}</button>
                    <button
                      v-if="session.status === 'complete'"
                      @click="viewReport(session.token)"
                      class="text-[10px] text-green-400 hover:text-green-300 px-1"
                      title="View report"
                    >Report</button>
                    <button
                      @click="deleteInterview(session.token)"
                      class="text-[10px] text-red-400 hover:text-red-300 px-1"
                      title="Delete session"
                    >✕</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Report viewer -->
            <div v-if="interviews.viewingReport" class="bg-gray-800 rounded p-3">
              <div class="flex items-center justify-between mb-2">
                <h4 class="text-xs font-medium text-gray-400">Report: {{ interviews.viewingReportName }}</h4>
                <button @click="interviews.viewingReport = null; interviews.viewingReportName = ''" class="text-xs text-gray-400 hover:text-white">Close</button>
              </div>
              <pre class="text-[10px] text-gray-300 whitespace-pre-wrap max-h-64 overflow-y-auto font-mono leading-relaxed">{{ interviews.viewingReport }}</pre>
              <button
                @click="copyReportText"
                class="mt-2 bg-gray-700 hover:bg-gray-600 text-white py-1 px-2 rounded text-xs transition-colors"
              >
                {{ interviews.reportCopied ? '✓ Copied' : 'Copy Report' }}
              </button>
            </div>
          </div>
        </div>

        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, computed, onMounted, watch } from 'vue'
import api from '../services/api'
import { useBasecampOAuth } from '../composables/useBasecampOAuth'

export default {
  name: 'DevPanel',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:5001/api'

    // Password gate state (persists for session — not reset on close)
    const devPanelAuth = reactive({
      required: null,    // null = unknown, true = password needed, false = open access
      verified: false,
      checking: false,
      password: '',
      error: ''
    })

    const checkDevPanelAccess = async () => {
      // Already verified this session
      if (devPanelAuth.verified) return true
      try {
        const res = await api.get('/external-services/devpanel-check')
        devPanelAuth.required = res.data.data.required
        if (!res.data.data.required) {
          devPanelAuth.verified = true
          return true
        }
        return false
      } catch {
        // If check fails, assume open access
        devPanelAuth.required = false
        devPanelAuth.verified = true
        return true
      }
    }

    const verifyDevPanelPassword = async () => {
      devPanelAuth.checking = true
      devPanelAuth.error = ''
      try {
        await api.post('/external-services/devpanel-verify', { password: devPanelAuth.password })
        devPanelAuth.verified = true
        devPanelAuth.password = ''
        // Now load panel data
        fetchStatus()
        fetchExternalServicesStatus()
        fetchLoginRestriction()
        fetchDriveConfig()
        loadFolderConfig()
        fetchInterviews()
      } catch (err) {
        devPanelAuth.error = err.response?.data?.error || 'Verification failed'
      } finally {
        devPanelAuth.checking = false
      }
    }

    // Accordion state — which sections are expanded
    const openSections = reactive(new Set())

    const toggleSection = (name) => {
      if (openSections.has(name)) {
        openSections.delete(name)
      } else {
        openSections.add(name)
      }
    }

    // ── Database diagnostics ──
    const dbInfo = reactive({
      loading: false,
      loaded: false,
      error: null,
      data: null
    })

    const fetchDbInfo = async () => {
      dbInfo.loading = true
      dbInfo.error = null
      try {
        const res = await api.get('/admin/system/db-info')
        dbInfo.data = res.data.data
        dbInfo.loaded = true
      } catch (err) {
        dbInfo.error = err.response?.data?.error || err.message || 'Failed to load DB info'
      } finally {
        dbInfo.loading = false
      }
    }

    const onDatabaseToggle = () => {
      toggleSection('database')
      if (openSections.has('database') && !dbInfo.loaded && !dbInfo.loading) {
        fetchDbInfo()
      }
    }

    const readyStateLabel = (state) => {
      const labels = { 0: 'disconnected', 1: 'connected', 2: 'connecting', 3: 'disconnecting' }
      return labels[state] || 'unknown'
    }

    const dbNameSource = computed(() => {
      const d = dbInfo.data
      if (!d) return ''
      if (d.usingDbNameOverride) return 'DB_NAME env var (Mongoose dbName option)'
      if (d.mongodbUriPath && d.mongodbUriPath !== '/' && d.mongodbUriPath !== '') {
        return `URI path component (${d.mongodbUriPath})`
      }
      return 'Mongoose default — no explicit DB name applied'
    })

    // ── Login Restriction ──
    const loginRestriction = reactive({
      loading: false,
      saving: false,
      saved: false,
      error: null,
      envConfigured: false,
      envEmails: [],
      enabled: false,
      allowedEmails: [],
      newEmail: ''
    })

    const fetchLoginRestriction = async () => {
      loginRestriction.loading = true
      loginRestriction.error = null
      try {
        const response = await api.get('/external-services/login-restriction')
        if (response.data.success) {
          const d = response.data.data
          loginRestriction.envConfigured = d.envConfigured
          loginRestriction.envEmails = d.envEmails
          loginRestriction.enabled = d.enabled
          loginRestriction.allowedEmails = d.allowedEmails || []
        }
      } catch (err) {
        loginRestriction.error = err.response?.data?.error || 'Failed to load'
      } finally {
        loginRestriction.loading = false
      }
    }

    const saveLoginRestriction = async () => {
      loginRestriction.saving = true
      loginRestriction.error = null
      loginRestriction.saved = false
      try {
        const response = await api.put('/external-services/login-restriction', {
          enabled: loginRestriction.enabled,
          allowedEmails: loginRestriction.allowedEmails
        })
        if (response.data.success) {
          loginRestriction.enabled = response.data.data.enabled
          loginRestriction.allowedEmails = response.data.data.allowedEmails
          loginRestriction.saved = true
          setTimeout(() => { loginRestriction.saved = false }, 2000)
        }
      } catch (err) {
        loginRestriction.error = err.response?.data?.error || 'Save failed'
      } finally {
        loginRestriction.saving = false
      }
    }

    const toggleLoginRestriction = () => {
      loginRestriction.enabled = !loginRestriction.enabled
      saveLoginRestriction()
    }

    const addAllowedEmail = () => {
      const email = loginRestriction.newEmail.trim().toLowerCase()
      if (!email || !email.includes('@')) return
      if (loginRestriction.allowedEmails.includes(email)) {
        loginRestriction.newEmail = ''
        return
      }
      loginRestriction.allowedEmails.push(email)
      loginRestriction.newEmail = ''
      saveLoginRestriction()
    }

    const removeAllowedEmail = (idx) => {
      loginRestriction.allowedEmails.splice(idx, 1)
      saveLoginRestriction()
    }

    // Computed: filtered phase raw fields based on search input
    const filteredPhaseRawFields = computed(() => {
      const search = synergist.phaseRawSearch.toLowerCase().trim()
      if (!search) return synergist.phaseRawFields.map(f => ({ ...f, matchesSearch: false }))
      return synergist.phaseRawFields
        .map(f => ({
          ...f,
          matchesSearch: String(f.value).toLowerCase().includes(search)
        }))
        .filter(f => f.matchesSearch || f.name.toLowerCase().includes(search))
    })

    // External services status (fetched from API)
    const externalServices = reactive({
      basecamp: { hasClientId: false, oauthConnected: false, identity: null, accounts: [] },
      googleDrive: { hasKeyFile: false, userConnected: false },
      googleSpaces: { hasKeyFile: false, userConnected: false }
    })

    const synergist = reactive({
      status: {
        enabled: false,
        readOnly: true,
        hasCredentials: false,
        testJobs: []
      },
      testing: false,
      result: null,
      loadingJobs: false,
      jobs: [],
      loadingStageTypes: false,
      stageTypes: [],
      // Stage details testing
      stageCodeInput: '',
      loadingStageDetails: false,
      stageDetailsError: null,
      stageDetails: null,
      // Lookup properties explorer
      loadingLookupProperties: false,
      lookupProperties: [],
      selectedLookupProperty: null,
      loadingLookupValues: false,
      lookupValues: [],
      // NEW: Raw lookup properties (custom field metadata)
      loadingLookupPropertiesRaw: false,
      lookupPropertiesRawError: null,
      lookupPropertiesRawModel: [],
      lookupPropertiesRawData: [],
      // NEW: Charge codes
      loadingChargeCodes: false,
      chargeCodes: [],
      chargeCodesError: null,
      // NEW: Phase model structure
      phaseModelInput: '',
      loadingPhaseModel: false,
      phaseModelError: null,
      phaseModelFields: [],
      // NEW: Phase raw data (field value discovery)
      phaseRawInput: '',
      loadingPhaseRaw: false,
      phaseRawError: null,
      phaseRawFields: [],
      phaseRawSearch: '',
      // NEW: Test phase field update
      testUpdateJobAndPhase: '',
      testUpdateUuid: '',
      testUpdateField: 'phaseComments',
      testUpdateValue: '',
      loadingTestUpdate: false,
      testUpdateError: null,
      testUpdateResult: null,
      // NEW: Test estimate update
      testEstimateIdType: 'internal',  // 'internal' or 'uuid'
      testEstimateInternal: '',
      testEstimateHours: '',
      testEstimateJobCode: '',
      testEstimateJobAndPhase: '',  // for before/after diff
      loadingTestEstimate: false,
      testEstimateError: null,
      testEstimateResult: null,
      // NEW: Estimates explorer
      estimatesInput: '',
      loadingEstimates: false,
      estimatesError: null,
      estimatesResult: null
    })

    // Basecamp state
    const basecamp = reactive({
      testing: false,
      result: null,
      testingSetup: false,
      setupResult: null,
      connecting: false,
      disconnectingOAuth: false
    })

    // Google Drive state
    const drive = reactive({
      testing: false,
      result: null,
      testingHierarchy: false,
      hierarchyResult: null,
      // Discovery state
      loadingSharedDrives: false,
      sharedDrives: [],
      sharedDriveId: null,
      loadingFolders: false,
      folders: [],
      // Mapping state
      clients: [],
      mappingFolder: null,
      savingMapping: false,
      // Folder navigator state
      navPath: [],
      navFolders: [],
      loadingNav: false,
      selectedClientFolder: null,
      // Folder config state
      folderConfig: {
        phaseSubfolders: [],
        deliverableSubfolders: [],
        jobSubfolders: [],
        loading: false,
        saving: false,
        newPhaseSubfolder: '',
        newDeliverableSubfolder: '',
        newJobSubfolder: ''
      }
    })

    // Knowledge Base state
    const kb = reactive({
      listing: false,
      files: [],
      listError: null,
      readResult: null,
      assembling: false,
      assembleResult: null,
      assembleError: null,
      estimating: false,
      estimateResult: null,
      estimateError: null,
      estimateUsage: null,
      refreshing: false,
      refreshError: null,
      refreshedAt: null
    })

    // Google Spaces state
    const spaces = reactive({
      testing: false,
      result: null,
      testingCreate: false,
      createResult: null,
      testingSetup: false,
      setupResult: null
    })

    // Trigger 1 combined test
    const trigger1 = reactive({
      testing: false,
      result: null
    })

    // Connection management (for testing)
    const connectionMgmt = reactive({
      phaseId: '',
      disconnecting: {
        basecamp: false,
        drive: false,
        spaces: false
      },
      result: null
    })

    // ── Discovery Interviews ──
    const interviews = reactive({
      newName: '',
      newRole: '',
      newEmail: '',
      creating: false,
      lastCreatedUrl: null,
      copied: false,
      loading: false,
      sessions: [],
      viewingReport: null,
      viewingReportName: '',
      reportCopied: false,
      forceCompleting: null
    })

    const createInterview = async () => {
      if (!interviews.newName.trim()) return
      interviews.creating = true
      interviews.lastCreatedUrl = null
      try {
        const response = await api.post('/interview/admin/create', {
          name: interviews.newName.trim(),
          role: interviews.newRole.trim(),
          email: interviews.newEmail.trim()
        })
        if (response.data.success) {
          interviews.lastCreatedUrl = response.data.data.url
          interviews.newName = ''
          interviews.newRole = ''
          interviews.newEmail = ''
          fetchInterviews()
        }
      } catch (error) {
        console.error('Failed to create interview:', error)
      } finally {
        interviews.creating = false
      }
    }

    const copyInterviewUrl = async () => {
      if (!interviews.lastCreatedUrl) return
      try {
        await navigator.clipboard.writeText(interviews.lastCreatedUrl)
        interviews.copied = true
        setTimeout(() => { interviews.copied = false }, 2000)
      } catch (e) {
        console.error('Copy failed:', e)
      }
    }

    const copySessionUrl = async (token) => {
      const url = `${window.location.origin}/interview?token=${token}`
      try {
        await navigator.clipboard.writeText(url)
      } catch (e) {
        console.error('Copy failed:', e)
      }
    }

    const fetchInterviews = async () => {
      interviews.loading = true
      try {
        const response = await api.get('/interview/admin/list')
        if (response.data.success) {
          interviews.sessions = response.data.data
        }
      } catch (error) {
        console.error('Failed to fetch interviews:', error)
      } finally {
        interviews.loading = false
      }
    }

    const viewReport = async (token) => {
      try {
        const response = await api.get(`/interview/admin/${token}/report`)
        if (response.data.success) {
          interviews.viewingReport = response.data.data.report
          interviews.viewingReportName = response.data.data.name
          interviews.reportCopied = false
        }
      } catch (error) {
        console.error('Failed to fetch report:', error)
      }
    }

    const copyReportText = async () => {
      if (!interviews.viewingReport) return
      try {
        await navigator.clipboard.writeText(interviews.viewingReport)
        interviews.reportCopied = true
        setTimeout(() => { interviews.reportCopied = false }, 2000)
      } catch (e) {
        console.error('Copy failed:', e)
      }
    }

    const forceCompleteInterview = async (token) => {
      interviews.forceCompleting = token
      try {
        const response = await api.post(`/interview/admin/${token}/force-complete`)
        if (response.data.success) {
          fetchInterviews()
        }
      } catch (error) {
        console.error('Failed to force-complete interview:', error)
      } finally {
        interviews.forceCompleting = null
      }
    }

    const deleteInterview = async (token) => {
      try {
        await api.delete(`/interview/admin/${token}`)
        interviews.sessions = interviews.sessions.filter(s => s.token !== token)
        if (interviews.viewingReport) {
          interviews.viewingReport = null
          interviews.viewingReportName = ''
        }
      } catch (error) {
        console.error('Failed to delete interview:', error)
      }
    }

    const close = () => {
      emit('close')
    }

    const fetchStatus = async () => {
      try {
        const response = await api.get('/synergist/status')
        if (response.data.success) {
          Object.assign(synergist.status, response.data.data)
        }
      } catch (error) {
        console.error('Failed to fetch Synergist status:', error)
      }
    }

    const fetchExternalServicesStatus = async () => {
      try {
        const response = await api.get('/external-services/status')
        if (response.data.success) {
          Object.assign(externalServices, response.data.data)
        }
      } catch (error) {
        console.error('Failed to fetch external services status:', error)
      }
    }

    // Basecamp methods
    const testBasecamp = async () => {
      basecamp.testing = true
      basecamp.result = null
      try {
        const response = await api.get('/external-services/basecamp/test')
        basecamp.result = response.data.data
      } catch (error) {
        basecamp.result = { connected: false, error: error.response?.data?.error || error.message }
      } finally {
        basecamp.testing = false
      }
    }

    const testBasecampFullSetup = async () => {
      basecamp.testingSetup = true
      basecamp.setupResult = null
      try {
        const response = await api.post('/external-services/basecamp/test-full-setup', {})
        basecamp.setupResult = response.data.data
      } catch (error) {
        basecamp.setupResult = { error: error.response?.data?.error || error.message }
      } finally {
        basecamp.testingSetup = false
      }
    }

    // Basecamp OAuth via shared composable
    const bcOAuth = useBasecampOAuth({
      onSuccess: () => fetchExternalServicesStatus()
    })
    // Bridge composable ref to DevPanel's reactive state for template compat
    watch(bcOAuth.connecting, (v) => { basecamp.connecting = v })

    const connectBasecamp = () => bcOAuth.connect()

    const disconnectBasecamp = async () => {
      basecamp.disconnectingOAuth = true
      try {
        await bcOAuth.disconnect()
        externalServices.basecamp.oauthConnected = false
        externalServices.basecamp.identity = null
        externalServices.basecamp.accounts = []
      } catch (error) {
        console.error('Failed to disconnect Basecamp:', error)
      } finally {
        basecamp.disconnectingOAuth = false
      }
    }

    // Google Drive methods
    const testDrive = async () => {
      drive.testing = true
      drive.result = null
      try {
        const response = await api.get('/external-services/drive/test')
        drive.result = response.data.data
      } catch (error) {
        drive.result = { connected: false, error: error.response?.data?.error || error.message }
      } finally {
        drive.testing = false
      }
    }

    const testDriveHierarchy = async () => {
      drive.testingHierarchy = true
      drive.hierarchyResult = null
      try {
        const response = await api.post('/external-services/drive/test-phase-hierarchy', {})
        drive.hierarchyResult = response.data.data
      } catch (error) {
        drive.hierarchyResult = { error: error.response?.data?.error || error.message }
      } finally {
        drive.testingHierarchy = false
      }
    }

    // Knowledge Base methods
    const refreshKbCache = async () => {
      kb.refreshing = true
      kb.refreshError = null
      try {
        const response = await api.post('/external-services/kb/refresh-cache')
        kb.refreshedAt = response.data.data.refreshedAt
      } catch (error) {
        kb.refreshError = error.response?.data?.error || error.message
      } finally {
        kb.refreshing = false
      }
    }

    const listKbFiles = async () => {
      kb.listing = true
      kb.files = []
      kb.listError = null
      kb.readResult = null
      try {
        const response = await api.get('/external-services/kb/list-files')
        kb.files = response.data.data.files
      } catch (error) {
        kb.listError = error.response?.data?.error || error.message
      } finally {
        kb.listing = false
      }
    }

    const readKbFile = async (file) => {
      kb.readResult = null
      try {
        const response = await api.get(`/external-services/kb/read-file/${file.id}`)
        const content = response.data.data.content
        kb.readResult = {
          path: file.relativePath,
          contentLength: content.length,
          preview: content.substring(0, 2000) + (content.length > 2000 ? '\n\n... (truncated)' : '')
        }
      } catch (error) {
        kb.readResult = { path: file.relativePath, contentLength: 0, preview: `Error: ${error.response?.data?.error || error.message}` }
      }
    }

    const testEstimation = async () => {
      kb.estimating = true
      kb.estimateResult = ''
      kb.estimateError = null
      kb.estimateUsage = null
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`${apiUrl}/external-services/kb/test-estimate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({})
        })

        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: response.statusText }))
          throw new Error(err.error || response.statusText)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() // keep incomplete line in buffer

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const data = JSON.parse(line.slice(6))
              if (data.type === 'text') {
                kb.estimateResult += data.text
              } else if (data.type === 'done') {
                kb.estimateUsage = data.usage
              } else if (data.type === 'error') {
                kb.estimateError = data.error
              }
            } catch (e) {
              // skip malformed lines
            }
          }
        }
      } catch (error) {
        kb.estimateError = error.message
      } finally {
        kb.estimating = false
      }
    }

    const assembleKbContext = async () => {
      kb.assembling = true
      kb.assembleResult = null
      kb.assembleError = null
      try {
        const response = await api.get('/external-services/kb/assemble-context/brief-to-estimation')
        kb.assembleResult = response.data.data
      } catch (error) {
        kb.assembleError = error.response?.data?.error || error.message
      } finally {
        kb.assembling = false
      }
    }

    // Drive discovery methods
    const fetchDriveConfig = async () => {
      try {
        const response = await api.get('/external-services/drive/config')
        if (response.data.success) {
          const { sharedDriveId, clientFolderId, clients } = response.data.data
          drive.sharedDriveId = sharedDriveId
          drive.clients = clients
          if (clientFolderId) {
            drive.selectedClientFolder = { id: clientFolderId, name: '(configured in .env)' }
          }
        }
      } catch (error) {
        console.error('Failed to fetch drive config:', error)
      }
    }

    const fetchSharedDrives = async () => {
      drive.loadingSharedDrives = true
      drive.sharedDrives = []
      try {
        const response = await api.get('/external-services/drive/shared-drives')
        if (response.data.success) {
          drive.sharedDrives = response.data.data.drives || []
        }
      } catch (error) {
        console.error('Failed to fetch shared drives:', error)
      } finally {
        drive.loadingSharedDrives = false
      }
    }

    const fetchDriveFolders = async (parentId) => {
      drive.loadingFolders = true
      drive.folders = []
      try {
        const response = await api.get(`/external-services/drive/folders/${parentId}`)
        if (response.data.success) {
          drive.folders = response.data.data.folders || []
        }
      } catch (error) {
        console.error('Failed to fetch folders:', error)
      } finally {
        drive.loadingFolders = false
      }
    }

    const startMappingFolder = (folder) => {
      drive.mappingFolder = folder
    }

    const cancelMappingFolder = () => {
      drive.mappingFolder = null
    }

    const mapFolderToClient = async (clientId) => {
      if (!drive.mappingFolder) return
      drive.savingMapping = true
      try {
        const response = await api.post('/external-services/drive/map-client-folder', {
          clientId,
          folderId: drive.mappingFolder.id,
          folderName: drive.mappingFolder.name
        })
        if (response.data.success) {
          const clientIndex = drive.clients.findIndex(c => c.id === clientId)
          if (clientIndex >= 0) {
            drive.clients[clientIndex].driveClientFolderId = drive.mappingFolder.id
          }
          drive.mappingFolder = null
        }
      } catch (error) {
        console.error('Failed to map folder to client:', error)
      } finally {
        drive.savingMapping = false
      }
    }

    const getClientForFolder = (folderId) => {
      return drive.clients.find(c => c.driveClientFolderId === folderId)
    }

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text)
        console.log('Copied to clipboard:', text)
      } catch (error) {
        console.error('Failed to copy:', error)
      }
    }

    // Folder navigator methods
    const startFolderNav = async () => {
      if (!drive.sharedDriveId) return
      drive.navPath = [{ id: drive.sharedDriveId, name: 'Shared Drive (root)' }]
      drive.selectedClientFolder = null
      await loadNavFolders(drive.sharedDriveId)
    }

    const loadNavFolders = async (parentId) => {
      drive.loadingNav = true
      drive.navFolders = []
      try {
        const response = await api.get(`/external-services/drive/folders/${parentId}`)
        if (response.data.success) {
          drive.navFolders = response.data.data.folders || []
        }
      } catch (error) {
        console.error('Failed to load nav folders:', error)
      } finally {
        drive.loadingNav = false
      }
    }

    const navOpenFolder = async (folder) => {
      drive.navPath.push({ id: folder.id, name: folder.name })
      await loadNavFolders(folder.id)
    }

    const navGoUp = async () => {
      if (drive.navPath.length <= 1) return
      drive.navPath.pop()
      const parentId = drive.navPath[drive.navPath.length - 1].id
      await loadNavFolders(parentId)
    }

    const navGoToLevel = async (index) => {
      if (index >= drive.navPath.length - 1) return
      drive.navPath = drive.navPath.slice(0, index + 1)
      const parentId = drive.navPath[drive.navPath.length - 1].id
      await loadNavFolders(parentId)
    }

    const navSelectFolder = (folder) => {
      drive.selectedClientFolder = { id: folder.id, name: folder.name }
      drive.navPath = []
      drive.navFolders = []
    }

    const navClearSelection = () => {
      drive.selectedClientFolder = null
    }

    const closeFolderNav = () => {
      drive.navPath = []
      drive.navFolders = []
      drive.selectedClientFolder = null
    }

    // Folder config methods
    const loadFolderConfig = async () => {
      drive.folderConfig.loading = true
      try {
        const response = await api.get('/external-services/drive/folder-config')
        if (response.data.success) {
          drive.folderConfig.phaseSubfolders = response.data.data.phaseSubfolders || []
          drive.folderConfig.deliverableSubfolders = response.data.data.deliverableSubfolders || []
          drive.folderConfig.jobSubfolders = response.data.data.jobSubfolders || ['Studio']
        }
      } catch (error) {
        console.error('Failed to load folder config:', error)
      } finally {
        drive.folderConfig.loading = false
      }
    }

    const saveFolderConfig = async () => {
      drive.folderConfig.saving = true
      try {
        await api.put('/external-services/drive/folder-config', {
          phaseSubfolders: drive.folderConfig.phaseSubfolders,
          deliverableSubfolders: drive.folderConfig.deliverableSubfolders,
          jobSubfolders: drive.folderConfig.jobSubfolders
        })
      } catch (error) {
        console.error('Failed to save folder config:', error)
      } finally {
        drive.folderConfig.saving = false
      }
    }

    const addPhaseSubfolder = async () => {
      const name = drive.folderConfig.newPhaseSubfolder.trim()
      if (!name || drive.folderConfig.phaseSubfolders.includes(name)) return
      drive.folderConfig.phaseSubfolders.push(name)
      drive.folderConfig.newPhaseSubfolder = ''
      await saveFolderConfig()
    }

    const removePhaseSubfolder = async (index) => {
      drive.folderConfig.phaseSubfolders.splice(index, 1)
      await saveFolderConfig()
    }

    const addDeliverableSubfolder = async () => {
      const name = drive.folderConfig.newDeliverableSubfolder.trim()
      if (!name || drive.folderConfig.deliverableSubfolders.includes(name)) return
      drive.folderConfig.deliverableSubfolders.push(name)
      drive.folderConfig.newDeliverableSubfolder = ''
      await saveFolderConfig()
    }

    const removeDeliverableSubfolder = async (index) => {
      drive.folderConfig.deliverableSubfolders.splice(index, 1)
      await saveFolderConfig()
    }

    const addJobSubfolder = async () => {
      const name = drive.folderConfig.newJobSubfolder.trim()
      if (!name || drive.folderConfig.jobSubfolders.includes(name)) return
      drive.folderConfig.jobSubfolders.push(name)
      drive.folderConfig.newJobSubfolder = ''
      await saveFolderConfig()
    }

    const removeJobSubfolder = async (index) => {
      drive.folderConfig.jobSubfolders.splice(index, 1)
      await saveFolderConfig()
    }

    // Google Spaces methods
    const testSpaces = async () => {
      spaces.testing = true
      spaces.result = null
      try {
        const response = await api.get('/external-services/spaces/test')
        spaces.result = response.data.data
      } catch (error) {
        spaces.result = { connected: false, error: error.response?.data?.error || error.message }
      } finally {
        spaces.testing = false
      }
    }

    const testSpacesCreate = async () => {
      spaces.testingCreate = true
      spaces.createResult = null
      try {
        const response = await api.post('/external-services/spaces/test-create-space', {
          displayName: 'TEST_SPACE_DWD',
          description: 'Testing domain-wide delegation for space creation'
        })
        spaces.createResult = response.data.data
      } catch (error) {
        spaces.createResult = { error: error.response?.data?.error || error.message }
      } finally {
        spaces.testingCreate = false
      }
    }

    const testSpacesFullSetup = async () => {
      spaces.testingSetup = true
      spaces.setupResult = null
      try {
        const response = await api.post('/external-services/spaces/test-full-setup', {
          welcomeMessage: 'Welcome to the test space! Created from DevPanel.'
        })
        spaces.setupResult = response.data.data
      } catch (error) {
        spaces.setupResult = { error: error.response?.data?.error || error.message }
      } finally {
        spaces.testingSetup = false
      }
    }

    // Full Trigger 1 test
    const testTrigger1 = async () => {
      trigger1.testing = true
      trigger1.result = null
      try {
        const response = await api.post('/external-services/test-trigger1', {})
        trigger1.result = response.data.data
      } catch (error) {
        trigger1.result = { errors: [{ service: 'all', error: error.response?.data?.error || error.message }] }
      } finally {
        trigger1.testing = false
      }
    }

    // Connection management methods
    const disconnectService = async (service) => {
      if (!connectionMgmt.phaseId) {
        connectionMgmt.result = { error: 'Phase ID is required' }
        return
      }

      connectionMgmt.disconnecting[service] = true
      connectionMgmt.result = null

      try {
        await api.post('/trigger1/disconnect', {
          phaseId: connectionMgmt.phaseId,
          service
        })
        connectionMgmt.result = { success: true, service, message: `${service} disconnected successfully` }
      } catch (error) {
        connectionMgmt.result = { error: error.response?.data?.error || error.message, service }
      } finally {
        connectionMgmt.disconnecting[service] = false
      }
    }

    const testSynergist = async () => {
      synergist.testing = true
      synergist.result = null
      try {
        const response = await api.get('/synergist/test')
        synergist.result = response.data.data
      } catch (error) {
        synergist.result = {
          connected: false,
          error: error.response?.data?.error || error.message
        }
      } finally {
        synergist.testing = false
      }
    }

    const fetchJobs = async () => {
      synergist.loadingJobs = true
      try {
        const response = await api.get('/synergist/jobs', { params: { rows: 10 } })
        if (response.data.success) {
          synergist.jobs = response.data.data.jobs || []
        }
      } catch (error) {
        console.error('Failed to fetch jobs:', error)
      } finally {
        synergist.loadingJobs = false
      }
    }

    const fetchStageTypes = async () => {
      synergist.loadingStageTypes = true
      try {
        const response = await api.get('/synergist/stage-types')
        if (response.data.success) {
          synergist.stageTypes = response.data.data || []
        }
      } catch (error) {
        console.error('Failed to fetch stage types:', error)
      } finally {
        synergist.loadingStageTypes = false
      }
    }

    const fetchStageDetails = async () => {
      if (!synergist.stageCodeInput.trim()) return
      synergist.loadingStageDetails = true
      synergist.stageDetailsError = null
      synergist.stageDetails = null
      try {
        const stageCode = encodeURIComponent(synergist.stageCodeInput.trim())
        const response = await api.get(`/synergist/stages/${stageCode}/details`)
        if (response.data.success) {
          synergist.stageDetails = response.data.data
          if (!synergist.stageDetails) {
            synergist.stageDetailsError = 'No stage found with that code'
          }
        } else {
          synergist.stageDetailsError = response.data.error || 'Failed to fetch stage details'
        }
      } catch (error) {
        synergist.stageDetailsError = error.response?.data?.error || error.message || 'Failed to fetch stage details'
        console.error('Failed to fetch stage details:', error)
      } finally {
        synergist.loadingStageDetails = false
      }
    }

    const fetchLookupProperties = async () => {
      synergist.loadingLookupProperties = true
      synergist.lookupProperties = []
      synergist.selectedLookupProperty = null
      synergist.lookupValues = []
      try {
        const response = await api.get('/synergist/lookup-properties')
        if (response.data.success) {
          synergist.lookupProperties = response.data.data || []
        }
      } catch (error) {
        console.error('Failed to fetch lookup properties:', error)
      } finally {
        synergist.loadingLookupProperties = false
      }
    }

    const selectLookupProperty = async (prop) => {
      synergist.selectedLookupProperty = prop
      synergist.loadingLookupValues = true
      synergist.lookupValues = []
      try {
        const response = await api.get(`/synergist/lookup-values/${prop.uuid}`)
        if (response.data.success) {
          synergist.lookupValues = response.data.data || []
        }
      } catch (error) {
        console.error('Failed to fetch lookup values:', error)
      } finally {
        synergist.loadingLookupValues = false
      }
    }

    // NEW: Fetch raw lookup properties (all metadata fields)
    const fetchLookupPropertiesRaw = async () => {
      synergist.loadingLookupPropertiesRaw = true
      synergist.lookupPropertiesRawError = null
      synergist.lookupPropertiesRawModel = []
      synergist.lookupPropertiesRawData = []
      try {
        const response = await api.get('/synergist/lookup-properties-raw')
        if (response.data.success) {
          synergist.lookupPropertiesRawModel = response.data.data.model || []
          synergist.lookupPropertiesRawData = response.data.data.data || []
        } else {
          synergist.lookupPropertiesRawError = response.data.error || 'Failed to fetch'
        }
      } catch (error) {
        synergist.lookupPropertiesRawError = error.response?.data?.error || error.message
        console.error('Failed to fetch raw lookup properties:', error)
      } finally {
        synergist.loadingLookupPropertiesRaw = false
      }
    }

    // NEW: Fetch charge codes (disciplines)
    const fetchChargeCodes = async () => {
      synergist.loadingChargeCodes = true
      synergist.chargeCodes = []
      synergist.chargeCodesError = null
      try {
        const response = await api.get('/synergist/charge-codes')
        if (response.data.success) {
          synergist.chargeCodes = response.data.data.chargeCodes || []
        }
      } catch (error) {
        synergist.chargeCodesError = error.response?.data?.error || error.message
        console.error('Failed to fetch charge codes:', error)
      } finally {
        synergist.loadingChargeCodes = false
      }
    }

    // NEW: Fetch phase model structure
    const fetchPhaseModelStructure = async () => {
      if (!synergist.phaseModelInput.trim()) return
      synergist.loadingPhaseModel = true
      synergist.phaseModelError = null
      synergist.phaseModelFields = []
      try {
        const jobAndPhase = encodeURIComponent(synergist.phaseModelInput.trim())
        const response = await api.get(`/synergist/phase-model-structure/${jobAndPhase}`)
        if (response.data.success) {
          // Parse the modelstructure response
          // style=modelstructure returns data[] where each element describes a phase field:
          //   item.data = field name, item.label = display label,
          //   item.editable = writable flag, item.fieldtype = field type,
          //   item.mandatory = required flag, item.displayorder = sort order
          const raw = response.data.data
          if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
            synergist.phaseModelFields = raw.data.map(item => ({
              name: item.data || '',
              label: item.label || item.data || '',
              type: item.fieldtype || 'unknown',
              editable: item.editable === true || item.editable === 'true' || item.editable === 1,
              mandatory: item.mandatory === true || item.mandatory === 'true' || item.mandatory === 1,
              highlight: /description|comment|note|custom/i.test(item.data || item.label || '')
            }))
          } else {
            // Fallback: show raw response keys for debugging
            synergist.phaseModelFields = Object.keys(raw).map(key => ({
              name: key,
              label: key,
              type: typeof raw[key],
              editable: false,
              mandatory: false,
              highlight: /description|comment|note|custom/i.test(key)
            }))
          }
        } else {
          synergist.phaseModelError = response.data.error || 'Failed to fetch model structure'
        }
      } catch (error) {
        synergist.phaseModelError = error.response?.data?.error || error.message
        console.error('Failed to fetch phase model structure:', error)
      } finally {
        synergist.loadingPhaseModel = false
      }
    }

    // NEW: Fetch raw phase data (all field values)
    const fetchPhaseRawData = async () => {
      if (!synergist.phaseRawInput.trim()) return
      synergist.loadingPhaseRaw = true
      synergist.phaseRawError = null
      synergist.phaseRawFields = []
      try {
        const jobAndPhase = encodeURIComponent(synergist.phaseRawInput.trim())
        const response = await api.get(`/synergist/phase-raw-data/${jobAndPhase}`)
        if (response.data.success) {
          const raw = response.data.data
          if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
            // First record contains all field values
            const record = raw.data[0]
            synergist.phaseRawFields = Object.entries(record).map(([key, value]) => ({
              name: key,
              value: value === null || value === undefined ? '' : String(value)
            }))
          } else {
            synergist.phaseRawError = 'No data returned for this phase'
          }
        } else {
          synergist.phaseRawError = response.data.error || 'Failed to fetch phase data'
        }
      } catch (error) {
        synergist.phaseRawError = error.response?.data?.error || error.message
        console.error('Failed to fetch phase raw data:', error)
      } finally {
        synergist.loadingPhaseRaw = false
      }
    }

    // NEW: Test writing to a phase field
    const testPhaseUpdate = async () => {
      synergist.loadingTestUpdate = true
      synergist.testUpdateError = null
      synergist.testUpdateResult = null
      try {
        const response = await api.post('/synergist/test-phase-update', {
          jobAndPhase: synergist.testUpdateJobAndPhase.trim(),
          phaseUuid: synergist.testUpdateUuid.trim(),
          fieldName: synergist.testUpdateField.trim(),
          value: synergist.testUpdateValue
        })
        if (response.data.success) {
          synergist.testUpdateResult = response.data.data
        } else {
          synergist.testUpdateError = response.data.error || 'Update failed'
        }
      } catch (error) {
        synergist.testUpdateError = error.response?.data?.error || error.message
        console.error('Failed to test phase update:', error)
      } finally {
        synergist.loadingTestUpdate = false
      }
    }

    // NEW: Test writing estimated hours
    const testEstimateUpdate = async () => {
      synergist.loadingTestEstimate = true
      synergist.testEstimateError = null
      synergist.testEstimateResult = null
      try {
        const payload = {
          hours: Number(synergist.testEstimateHours),
          jobCode: synergist.testEstimateJobCode.trim() || null,
          idType: synergist.testEstimateIdType,
          jobAndPhase: synergist.testEstimateJobAndPhase.trim() || null
        }
        if (synergist.testEstimateIdType === 'uuid') {
          payload.timesummaryUuid = synergist.testEstimateInternal.trim()
        } else {
          payload.timesummaryInternal = Number(synergist.testEstimateInternal)
        }
        const response = await api.post('/synergist/test-estimate-update', payload)
        if (response.data.success) {
          synergist.testEstimateResult = response.data.data
        } else {
          synergist.testEstimateError = response.data.error || 'Update failed'
        }
      } catch (error) {
        synergist.testEstimateError = error.response?.data?.error || error.message
        console.error('Failed to test estimate update:', error)
      } finally {
        synergist.loadingTestEstimate = false
      }
    }

    // NEW: Fetch estimates for a phase
    const fetchEstimates = async () => {
      if (!synergist.estimatesInput.trim()) return
      synergist.loadingEstimates = true
      synergist.estimatesError = null
      synergist.estimatesResult = null
      try {
        const jobAndPhase = encodeURIComponent(synergist.estimatesInput.trim())
        const response = await api.get(`/synergist/estimates/${jobAndPhase}`)
        if (response.data.success) {
          synergist.estimatesResult = response.data.data.estimates || []
        } else {
          synergist.estimatesError = response.data.error || 'Failed to fetch estimates'
        }
      } catch (error) {
        synergist.estimatesError = error.response?.data?.error || error.message
        console.error('Failed to fetch estimates:', error)
      } finally {
        synergist.loadingEstimates = false
      }
    }

    onMounted(() => {
      if (props.isOpen) {
        fetchStatus()
        fetchExternalServicesStatus()
        fetchLoginRestriction()
      }
    })

    // Re-fetch status when panel opens
    const onOpen = async () => {
      const hasAccess = await checkDevPanelAccess()
      if (!hasAccess) return  // Show password form, don't fetch data yet
      fetchStatus()
      fetchExternalServicesStatus()
      fetchLoginRestriction()
      fetchDriveConfig()
      loadFolderConfig()
      fetchInterviews()
    }

    return {
      apiUrl,
      devPanelAuth,
      verifyDevPanelPassword,
      openSections,
      toggleSection,
      dbInfo,
      fetchDbInfo,
      onDatabaseToggle,
      readyStateLabel,
      dbNameSource,
      loginRestriction,
      fetchLoginRestriction,
      toggleLoginRestriction,
      addAllowedEmail,
      removeAllowedEmail,
      synergist,
      externalServices,
      basecamp,
      drive,
      kb,
      refreshKbCache,
      listKbFiles,
      readKbFile,
      assembleKbContext,
      testEstimation,
      spaces,
      trigger1,
      close,
      testSynergist,
      fetchJobs,
      fetchStageTypes,
      fetchStageDetails,
      fetchLookupProperties,
      fetchLookupPropertiesRaw,
      selectLookupProperty,
      fetchChargeCodes,
      fetchPhaseModelStructure,
      fetchPhaseRawData,
      filteredPhaseRawFields,
      testPhaseUpdate,
      testEstimateUpdate,
      fetchEstimates,
      testBasecamp,
      testBasecampFullSetup,
      connectBasecamp,
      disconnectBasecamp,
      testDrive,
      testDriveHierarchy,
      fetchSharedDrives,
      fetchDriveFolders,
      startMappingFolder,
      cancelMappingFolder,
      mapFolderToClient,
      getClientForFolder,
      copyToClipboard,
      startFolderNav,
      navOpenFolder,
      navGoUp,
      navGoToLevel,
      navSelectFolder,
      navClearSelection,
      closeFolderNav,
      loadFolderConfig,
      addPhaseSubfolder,
      removePhaseSubfolder,
      addDeliverableSubfolder,
      removeDeliverableSubfolder,
      addJobSubfolder,
      removeJobSubfolder,
      testSpaces,
      testSpacesCreate,
      testSpacesFullSetup,
      testTrigger1,
      connectionMgmt,
      disconnectService,
      interviews,
      createInterview,
      copyInterviewUrl,
      copySessionUrl,
      fetchInterviews,
      viewReport,
      copyReportText,
      deleteInterview,
      forceCompleteInterview,
      onOpen
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.onOpen()
      }
    }
  }
}
</script>
